import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { idpService } from '../services/idpService';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { AxiosError } from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GavelIcon from '@mui/icons-material/Gavel';
import SchoolIcon from '@mui/icons-material/School';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { AppDatePicker } from '../components/AppDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { createShouldDisableDate } from '../utils/dateValidation';
import { useDateLocale } from '../hooks/useDateLocale';
import { usePublicHolidays } from '../hooks/usePublicHolidays';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  getSuggestedReviewCycleName,
} from '../utils/fiscalYear';
import { fiscalYearService } from '../services/fiscalYearService';
import { formatDate, formatDateRange, formatCalendarDateIso, parseUtcDate, utcMs } from '../utils/dateFormat';
import {
  buildSuggestedReviewCycleDates,
  dovetailAdjustmentMessage,
} from '../utils/reviewCycleDates';
import { formatReviewStatus, getReviewStatusColor } from '../utils/reviewStatus';
import AppLayout from '../components/AppLayout';
import DashboardHelpSidebar from '../components/DashboardHelpSidebar';
import { useAgentContext } from '../contexts/AgentContext';
import { usePageAgentContext } from '../hooks/usePageAgentContext';
import ValuesBanner from '../components/ValuesBanner';
import PersonalMilestoneTiles from '../components/PersonalMilestoneTiles';
import type {
  EmployeeCheckInDashboard,
  ManagerCheckInDashboard,
  AdminCheckInDashboard
} from '../types';
import type { IndividualDevelopmentPlan } from '../types/idp';
import { IdpStatus } from '../types/idp';
import { RoleTemplateState } from '../types';
import { API_ENDPOINTS, EXPENSE_CLAIMS_ENABLED } from '../constants/api';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import StarsIcon from '@mui/icons-material/Stars';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotCreate, genericCopy } from '../utils/userFacingCopy';
import {
  employeeManagerOptedOutMessage,
  employeeManagerOptedOutShortLabel,
} from '../utils/checkInOptOutCopy';

const STATUTORY_ANNUAL_LEAVE_DAYS = 15;

interface LeaveReportEntry {
  employeeId: string;
  employeeName: string;
  departmentName?: string;
  businessUnitName?: string;
  leaveTypeName: string;
  daysTaken: number;
  periodYear: number;
  statutoryEntitlementMet: boolean;
}

interface LeaveReportResponse {
  entries: LeaveReportEntry[];
}

interface StatutoryAnnualLeaveStatusEntry {
  employeeId: string;
  employeeName: string;
  departmentName?: string;
  annualDaysTaken: number;
  daysStillToTake: number;
  countryCode: string;
  statutoryAnnualDays: number;
}

interface LeaveTypeDto {
  id: string;
  name: string;
  code: string;
  countsTowardAnnualCap: boolean;
}

interface ReviewCycle {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  employeeCutoff?: string;
  isActive: boolean;
  createdAt: string;
}

interface Review {
  id: string;
  reviewCycleId: string;
  reviewCycleName?: string;
  employeeId: string;
  employeeName?: string;
  departmentId?: string;
  departmentName?: string;
  managerId?: string;
  managerName?: string;
  status: string | number;
  finalisedAt?: string;
  objectiveAverage?: number;
  behaviouralAverage?: number;
  combinedRating?: number;
  finalRating?: number | string;
}

interface ReviewFeedbackAttestationItem {
  reviewId: string;
  employeeName: string;
  reviewCycleName: string;
  feedbackAttestation: number;
  feedbackAttestationComment?: string | null;
}

interface ManagerNeedingCoaching {
  managerId: string;
  managerName: string;
  count: number;
  reviews: ReviewFeedbackAttestationItem[];
}

interface DashboardReporting {
  currentCycleId?: string;
  currentCycleName?: string;
  daysRemaining?: number;
  departmentCount?: number;
  /** Distinct counts for org-level green cards (avoids double-counting users in multiple departments) */
  totalPendingCount?: number;
  totalCompletedCount?: number;
  totalExcludedCount?: number;
  /** Organisation completion as weighted progress (0/25/50/75/100% per stage). When present, use instead of binary completed/total. */
  orgWeightedCompletionRate?: number | null;
  pendingReviewsByDepartment: DepartmentReviewSummary[];
  completedReviewsByDepartment: DepartmentReviewSummary[];
  excludedReviewsByDepartment: DepartmentReviewSummary[];
  /** Managers whose direct reports indicated limited/little feedback in finalised reviews (not visible to managers) */
  managersNeedingCoaching?: ManagerNeedingCoaching[];
}

interface DepartmentReviewSummary {
  departmentId?: string;
  departmentName?: string;
  count: number;
  reviews: Review[];
  /** Status breakdown from API; used when reviews are withheld (e.g. senior manager) so table still shows counts */
  notStartedCount?: number;
  inProgressCount?: number;
  pendingCount?: number;
  disputedCount?: number;
  completeCount?: number;
  excludedCount?: number;
}

interface IdpDashboardReporting {
  departmentCount?: number;
  idpsByDepartment: DepartmentIdpSummary[];
  pendingIdpsByDepartment: DepartmentIdpSummary[];
  activeIdpsByDepartment: DepartmentIdpSummary[];
  employeeIdpStatuses: EmployeeIdpStatus[];
  totalIdps: number;
  totalPendingIdps: number;
  totalActiveIdps: number;
  totalOutstandingIdps: number;
  totalNotSubmittedIdps: number;
  totalDraftIdps: number;
}

interface DepartmentIdpSummary {
  departmentId?: string;
  departmentName?: string;
  count: number;
  pendingCount: number;
  activeCount: number;
  outstandingCount: number;
  notSubmittedCount: number;
  draftCount: number;
  expectedEmployeeCount: number;
}

interface EmployeeIdpStatus {
  employeeId: string;
  employeeName: string;
  employeeEmail?: string;
  idpId?: string;
  year?: number;
  status?: string;
  hasIdp: boolean;
}

interface CreateReviewCycleForm {
  name: string;
  startDate: Date | null;
  endDate: Date | null;
  employeeCutoff: Date | null;
}

interface UserDetailsData {
  pendingRoleTemplateId?: string;
  roleTemplateChangeManagerAcknowledgedAt?: string;
  roleTemplateChangeEmployeeAcknowledgedAt?: string;
  dateOfBirth?: string;
  dateOfEngagement?: string;
  companyName?: string;
}

interface RoleTemplateData {
  name?: string;
  state?: number | string;
}

interface TeamMemberData {
  pendingRoleTemplateId?: string;
  roleTemplateChangeManagerAcknowledgedAt?: string;
}

type DashboardView = 'my' | 'team' | 'organisation';

export default function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { viewType } = useParams<{ viewType?: DashboardView }>();
  const { user, outstandingTasksCount, openDisputesCount } = useAuth();
  const { showLegacyHelp } = useAgentContext();
  const country = useDateLocale();
  const { isHoliday } = usePublicHolidays(country);
  const shouldDisableDate = createShouldDisableDate(isHoliday);

  const [loading, setLoading] = useState(true);
  const [reviewCycles, setReviewCycles] = useState<ReviewCycle[]>([]);
  const [myReviews, setMyReviews] = useState<Review[]>([]);
  const [managedReviews, setManagedReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [dashboardReportingLoading, setDashboardReportingLoading] = useState(false);
  const [dashboardReporting, setDashboardReporting] = useState<DashboardReporting | null>(null);
  const [idpDashboardReporting, setIdpDashboardReporting] = useState<IdpDashboardReporting | null>(null);
  const [teamIdpDashboardReporting, setTeamIdpDashboardReporting] = useState<IdpDashboardReporting | null>(null);
  const [employeeCheckInData, setEmployeeCheckInData] = useState<EmployeeCheckInDashboard | null>(null);
  const [managerCheckInData, setManagerCheckInData] = useState<ManagerCheckInDashboard | null>(null);
  const [adminCheckInData, setAdminCheckInData] = useState<AdminCheckInDashboard | null>(null);
  const [error, setError] = useState<string>('');
  const [userDetails, setUserDetails] = useState<UserDetailsData | null>(null);
  const [pendingRoleTemplate, setPendingRoleTemplate] = useState<RoleTemplateData | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMemberData[]>([]);
  const [myLeaveBurndown, setMyLeaveBurndown] = useState<{ annualDaysTaken: number; statutoryMet: boolean } | null>(null);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeDto[]>([]);
  const [myLeaveDaysByType, setMyLeaveDaysByType] = useState<Record<string, number>>({});
  const [teamLeaveBurndown, setTeamLeaveBurndown] = useState<{ employeeId: string; employeeName: string; annualDaysTaken: number }[] | null>(null);
  const [teamLeaveOver20, setTeamLeaveOver20] = useState<{ employeeId: string; employeeName: string; annualDaysTaken: number }[] | null>(null);
  const [orgLeaveAtRisk, setOrgLeaveAtRisk] = useState<StatutoryAnnualLeaveStatusEntry[] | null>(null);
  const [pendingIdpApprovalsCount, setPendingIdpApprovalsCount] = useState<number>(0);
  const [currentIdp, setCurrentIdp] = useState<IndividualDevelopmentPlan | null>(null);
  const [useCalendarYear, setUseCalendarYear] = useState<boolean>(false);
  const [currentFiscalYear, setCurrentFiscalYear] = useState<number | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState<CreateReviewCycleForm>({
    name: '',
    startDate: null,
    endDate: null,
    employeeCutoff: null,
  });
  const [createError, setCreateError] = useState<string>('');
  const [createDovetailInfo, setCreateDovetailInfo] = useState<string>('');
  const [helpExpanded, setHelpExpanded] = useState(false);
  /** Cycle filter for dashboard stats: '' = all active cycles, or a cycle id to show one cycle only. */
  const [dashboardCycleFilter, setDashboardCycleFilter] = useState<string>('');
  // Manager review load table (org dashboard): filter, sort, pagination
  const [managerTableSearch, setManagerTableSearch] = useState('');
  const [managerTableSortBy, setManagerTableSortBy] = useState<'managerName' | 'directReportCount' | 'rag'>('directReportCount');
  const [managerTableSortDirection, setManagerTableSortDirection] = useState<'asc' | 'desc'>('desc');
  const [managerTablePage, setManagerTablePage] = useState(0);
  const [managerTableRowsPerPage, setManagerTableRowsPerPage] = useState(10);
  const [managerReviewLoadExpanded, setManagerReviewLoadExpanded] = useState(false);
  // Review Status by Department table (org dashboard) search, sort, pagination
  const [deptReviewTableSearch, setDeptReviewTableSearch] = useState('');
  const [deptReviewTableSortBy, setDeptReviewTableSortBy] = useState<'departmentName' | 'notStarted' | 'inProgress' | 'pending' | 'disputed' | 'complete' | 'excluded'>('complete');
  const [deptReviewTableSortDirection, setDeptReviewTableSortDirection] = useState<'asc' | 'desc'>('desc');
  const [deptReviewTablePage, setDeptReviewTablePage] = useState(0);
  const [deptReviewTableRowsPerPage, setDeptReviewTableRowsPerPage] = useState(10);
  // IDP by Department table (org dashboard) search, sort, pagination
  const [idpDeptTableSearch, setIdpDeptTableSearch] = useState('');
  const [idpDeptTableSortBy, setIdpDeptTableSortBy] = useState<'departmentName' | 'total' | 'pending' | 'active' | 'outstanding'>('outstanding');
  const [idpDeptTableSortDirection, setIdpDeptTableSortDirection] = useState<'asc' | 'desc'>('desc');
  const [idpDeptTablePage, setIdpDeptTablePage] = useState(0);
  const [idpDeptTableRowsPerPage, setIdpDeptTableRowsPerPage] = useState(10);
  // Statutory leave at risk table (org dashboard) search, sort, pagination
  const [orgLeaveTableSearch, setOrgLeaveTableSearch] = useState('');
  const [orgLeaveTableSortBy, setOrgLeaveTableSortBy] = useState<'departmentName' | 'employeeName' | 'daysTaken' | 'daysToTake'>('daysToTake');
  const [orgLeaveTableSortDirection, setOrgLeaveTableSortDirection] = useState<'asc' | 'desc'>('desc');
  const [orgLeaveTablePage, setOrgLeaveTablePage] = useState(0);
  const [orgLeaveTableRowsPerPage, setOrgLeaveTableRowsPerPage] = useState(10);
  const loadingRef = useRef(false);
  const reviewsRequestIdRef = useRef(0);
  const dashboardReportingRequestIdRef = useRef(0);
  const managerReviewLoadSectionRef = useRef<HTMLDivElement>(null);


  const getManagerReviewLoadRag = useCallback((directReportCount: number): 'green' | 'amber' | 'red' => {
    if (directReportCount <= 6) return 'green';
    if (directReportCount <= 9) return 'amber';
    return 'red';
  }, []);

  // Manager review load table: filter by name, sort, then paginate (for org dashboard)
  const managerTableFilteredAndSorted = useMemo(() => {
    const list = adminCheckInData?.managerSummaries ?? [];
    const query = managerTableSearch.trim().toLowerCase();
    const filtered = query
      ? list.filter((m) => m.managerName.toLowerCase().includes(query))
      : list;
    const sorted = [...filtered].sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;
      switch (managerTableSortBy) {
        case 'managerName':
          aVal = a.managerName;
          bVal = b.managerName;
          break;
        case 'directReportCount':
          aVal = a.directReportCount;
          bVal = b.directReportCount;
          break;
        case 'rag':
          {
            const ragWeight = (count: number) => {
              const rag = getManagerReviewLoadRag(count);
              if (rag === 'red') return 3;
              if (rag === 'amber') return 2;
              return 1;
            };
            aVal = ragWeight(a.directReportCount);
            bVal = ragWeight(b.directReportCount);
          }
          break;
        default:
          return 0;
      }
      if (aVal < bVal) return managerTableSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return managerTableSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [adminCheckInData?.managerSummaries, getManagerReviewLoadRag, managerTableSearch, managerTableSortBy, managerTableSortDirection]);

  const managerReviewLoadSummary = useMemo(() => {
    const summaries = adminCheckInData?.managerSummaries ?? [];
    const counts = { green: 0, amber: 0, red: 0 };
    for (const manager of summaries) {
      const rag = getManagerReviewLoadRag(manager.directReportCount);
      counts[rag] += 1;
    }
    return {
      ...counts,
      overThreshold: counts.amber + counts.red,
    };
  }, [adminCheckInData?.managerSummaries, getManagerReviewLoadRag]);

  const managerTablePaginated = useMemo(() => {
    const start = managerTablePage * managerTableRowsPerPage;
    return managerTableFilteredAndSorted.slice(start, start + managerTableRowsPerPage);
  }, [managerTableFilteredAndSorted, managerTablePage, managerTableRowsPerPage]);

  const handleManagerTableSort = (column: typeof managerTableSortBy) => {
    if (managerTableSortBy === column) {
      setManagerTableSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setManagerTableSortBy(column);
      setManagerTableSortDirection(column === 'managerName' ? 'asc' : 'desc');
    }
    setManagerTablePage(0);
  };

  // Calculate team members needing manager acknowledgment
  const teamMembersNeedingAcknowledgment = useMemo(() => {
    if (!teamMembers.length) return [];

    return teamMembers.filter((member) => {
      // Member has pending role template change
      if (!member.pendingRoleTemplateId) return false;

      // Manager hasn't acknowledged yet
      if (member.roleTemplateChangeManagerAcknowledgedAt) return false;

      return true;
    });
  }, [teamMembers]);

  // Stable key so loadDashboardData (and thus initial load + visibility refetch) only changes when roles actually change, not on every context re-render
  const userRolesKey = useMemo(() => (user?.roles ?? []).slice().sort().join(','), [user?.roles]);

  const loadDashboardData = useCallback(async () => {
    if (!user?.id) {
      return;
    }

    // Prevent multiple simultaneous calls
    if (loadingRef.current) {
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      setError('');

      const userRoles = userRolesKey ? userRolesKey.split(',') : [];
      const isAdminOrSM = userRoles.includes('Administrator') || userRoles.includes('SeniorManager');
      const isManagerOrAbove = userRoles.some((r: string) => r === 'Manager' || r === 'SeniorManager' || r === 'Administrator' || r === 'TeamLead');
      const isAdmin = userRoles.includes('Administrator');
      const currentYear = new Date().getFullYear();

      // Helper: GET with a typed fallback — swallows all errors
      const safeGet = async <T,>(url: string, fallback: T): Promise<T> => {
        try {
          const res = await apiService.get<T>(url);
          return res.data ?? fallback;
        } catch {
          return fallback;
        }
      };

      // Review cycles need special error handling to surface non-auth failures
      const fetchReviewCycles = async (): Promise<ReviewCycle[]> => {
        try {
          const res = await apiService.get<ReviewCycle[]>('/review-cycles');
          return (res.data || []).slice().sort(
            (a, b) => utcMs(a.startDate) - utcMs(b.startDate)
          );
        } catch (err: unknown) {
          const axiosError = err as AxiosError;
          if (axiosError.response?.status !== 403 && axiosError.response?.status !== 401) {
            setError(genericCopy.reviewCycles);
          }
          return [];
        }
      };

      // ── Phase 1: fire all independent calls concurrently ────────────────
      const cyclesPromise = fetchReviewCycles();
      const idpReportingPromise = isAdminOrSM
        ? safeGet<IdpDashboardReporting | null>('/individual-development-plans/dashboard-reporting', null)
        : Promise.resolve<IdpDashboardReporting | null>(null);
      const teamIdpReportingPromise = isManagerOrAbove
        ? safeGet<IdpDashboardReporting | null>('/individual-development-plans/team-dashboard-reporting', null)
        : Promise.resolve<IdpDashboardReporting | null>(null);
      const adminCheckInPromise = isAdminOrSM
        ? safeGet<AdminCheckInDashboard | null>(API_ENDPOINTS.CHECK_INS.DASHBOARD_ADMIN, null)
        : Promise.resolve<AdminCheckInDashboard | null>(null);
      const managerCheckInPromise = isManagerOrAbove
        ? safeGet<ManagerCheckInDashboard | null>(API_ENDPOINTS.CHECK_INS.DASHBOARD_MANAGER, null)
        : Promise.resolve<ManagerCheckInDashboard | null>(null);
      const pendingIdpCountPromise = isManagerOrAbove
        ? idpService.getPendingApprovalsCount().catch(() => 0)
        : Promise.resolve(0);
      const teamPromise = isManagerOrAbove
        ? safeGet<TeamMemberData[]>(API_ENDPOINTS.USERS.MY_TEAM, [])
        : Promise.resolve<TeamMemberData[]>([]);
      const employeeCheckInPromise = safeGet<EmployeeCheckInDashboard | null>(API_ENDPOINTS.CHECK_INS.DASHBOARD_EMPLOYEE, null);
      const myLeavePromise = safeGet<LeaveReportResponse | null>(`${API_ENDPOINTS.LEAVE.MY_REPORT}?periodYear=${currentYear}`, null);
      const leaveTypesPromise = safeGet<LeaveTypeDto[]>(API_ENDPOINTS.LEAVE_REQUESTS.LEAVE_TYPES, []);
      const orgLeavePromise = isAdminOrSM
        ? safeGet<StatutoryAnnualLeaveStatusEntry[] | null>(API_ENDPOINTS.LEAVE.STATUTORY_ANNUAL_STATUS(currentYear), null)
        : Promise.resolve<StatutoryAnnualLeaveStatusEntry[] | null>(null);
      const settingsPromise = isAdmin
        ? safeGet<{ useCalendarYear: boolean } | null>('/System/review-cycle-settings', null)
        : Promise.resolve<{ useCalendarYear: boolean } | null>(null);
      const idpsPromise = idpService.getMyIdps().catch(() => [] as IndividualDevelopmentPlan[]);
      const fiscalYearPromise = fiscalYearService.getCurrent().catch(() => null);
      const userDetailsPromise = safeGet<UserDetailsData | null>(API_ENDPOINTS.USERS.ME, null);

      const [
        sortedCycles,
        idpReportingData,
        teamIdpReportingData,
        adminCheckInResult,
        managerCheckInResult,
        pendingIdpCount,
        teamData,
        employeeCheckInResult,
        myLeaveData,
        leaveTypesData,
        orgLeaveData,
        settingsData,
        allIdps,
        fiscalYearData,
        userDetailsData,
      ] = await Promise.all([
        cyclesPromise,
        idpReportingPromise,
        teamIdpReportingPromise,
        adminCheckInPromise,
        managerCheckInPromise,
        pendingIdpCountPromise,
        teamPromise,
        employeeCheckInPromise,
        myLeavePromise,
        leaveTypesPromise,
        orgLeavePromise,
        settingsPromise,
        idpsPromise,
        fiscalYearPromise,
        userDetailsPromise,
      ]);

      // ── Apply Phase 1 results ────────────────────────────────────────────
      // My reviews and managed reviews are loaded in a separate effect so they respect the cycle filter.
      setReviewCycles(sortedCycles);
      setIdpDashboardReporting(idpReportingData);
      setTeamIdpDashboardReporting(teamIdpReportingData);
      setAdminCheckInData(adminCheckInResult);
      setManagerCheckInData(managerCheckInResult);
      setPendingIdpApprovalsCount(pendingIdpCount);
      const hasTeamAccess = teamData.length > 0;
      setTeamMembers(teamData);
      setEmployeeCheckInData(employeeCheckInResult);

      // My leave burndown and days by type
      setLeaveTypes(leaveTypesData ?? []);
      if (myLeaveData) {
        const entries = myLeaveData.entries ?? [];
        const isAnnual = (e: LeaveReportEntry) => e.leaveTypeName === 'Annual' || e.leaveTypeName === 'Annual Leave';
        const annualEntries = entries.filter(isAnnual);
        const annualDaysTaken = annualEntries.reduce((sum: number, e: LeaveReportEntry) => sum + e.daysTaken, 0);
        const statutoryMet = annualEntries.some((e: LeaveReportEntry) => e.statutoryEntitlementMet) || annualDaysTaken >= STATUTORY_ANNUAL_LEAVE_DAYS;
        setMyLeaveBurndown({ annualDaysTaken, statutoryMet });
        const byType: Record<string, number> = {};
        for (const e of entries) {
          const key = e.leaveTypeName ?? 'Other';
          byType[key] = (byType[key] ?? 0) + e.daysTaken;
        }
        setMyLeaveDaysByType(byType);
      } else {
        setMyLeaveBurndown(null);
        setMyLeaveDaysByType({});
      }

      // Org leave at risk
      if (orgLeaveData) {
        setOrgLeaveAtRisk(orgLeaveData.filter((row) => row.daysStillToTake > 0));
      } else {
        setOrgLeaveAtRisk(null);
      }

      // Calendar year / fiscal year settings
      const calendarYear = settingsData?.useCalendarYear ?? false;
      setUseCalendarYear(calendarYear);
      setCurrentFiscalYear(calendarYear ? null : (fiscalYearData?.fiscalYear ?? null));

      // User details
      setUserDetails(userDetailsData);

      // ── Phase 2: calls that depend on Phase 1 results (run concurrently) ─
      const phase2: Promise<void>[] = [];

      // Team leave depends on hasTeamAccess (align with Team Leave: managers default to direct reports only)
      const teamLeaveDirectOnlyParam = userRoles.includes('Manager') ? '&directReportsOnly=true' : '';
      if (hasTeamAccess) {
        phase2.push((async () => {
          try {
            const teamLeaveData = (await apiService.get<LeaveReportResponse>(
              `${API_ENDPOINTS.LEAVE.TEAM_REPORT}?periodYear=${currentYear}${teamLeaveDirectOnlyParam}`
            )).data;
            const teamEntries = teamLeaveData?.entries ?? [];
            const byEmployee = new Map<string, { employeeName: string; annualDaysTaken: number }>();
            for (const e of teamEntries) {
              if (e.leaveTypeName !== 'Annual' && e.leaveTypeName !== 'Annual Leave') continue;
              const existing = byEmployee.get(e.employeeId);
              if (existing) {
                existing.annualDaysTaken += e.daysTaken;
              } else {
                byEmployee.set(e.employeeId, { employeeName: e.employeeName, annualDaysTaken: e.daysTaken });
              }
            }
            const allTeam = Array.from(byEmployee.entries()).map(([employeeId, v]) => ({ employeeId, employeeName: v.employeeName, annualDaysTaken: v.annualDaysTaken }));
            setTeamLeaveBurndown(allTeam.filter(([, v]) => v.annualDaysTaken < STATUTORY_ANNUAL_LEAVE_DAYS));
            setTeamLeaveOver20(allTeam.filter(([, v]) => v.annualDaysTaken > 20));
          } catch {
            setTeamLeaveBurndown(null);
            setTeamLeaveOver20(null);
          }
        })());
      } else {
        setTeamLeaveBurndown(null);
        setTeamLeaveOver20(null);
      }

      // Pending role template depends on user details
      if (userDetailsData?.pendingRoleTemplateId) {
        phase2.push((async () => {
          try {
            const res = await apiService.get<RoleTemplateData>(`${API_ENDPOINTS.ROLES.BASE}/${userDetailsData.pendingRoleTemplateId}`);
            setPendingRoleTemplate(res.data);
          } catch {
            setPendingRoleTemplate(null);
          }
        })());
      } else {
        setPendingRoleTemplate(null);
      }

      await Promise.all(phase2);

      // ── IDP selection (depends on sortedCycles + calendarYear + allIdps) ─
      if (allIdps.length > 0) {
        try {
          const activeCycle = sortedCycles.find(cycle => cycle.isActive);
          let relevantIdp: IndividualDevelopmentPlan | null = null;

          if (activeCycle) {
            let cycleYear: number;
            if (calendarYear) {
              cycleYear = parseUtcDate(activeCycle.startDate)?.getFullYear() ?? new Date().getFullYear();
            } else {
              const evalResult = await fiscalYearService.evaluateDate(activeCycle.startDate);
              cycleYear = evalResult.fiscalYear;
            }
            relevantIdp = allIdps.find(idp => idp.year === cycleYear) || null;
          }

          if (!relevantIdp) {
            const idpYear = calendarYear
              ? currentYear
              : (fiscalYearData?.fiscalYear ?? (await fiscalYearService.getCurrent()).fiscalYear);
            relevantIdp = allIdps.find(idp => idp.year === idpYear) || allIdps[0];
          }

          setCurrentIdp(relevantIdp);
        } catch {
          setCurrentIdp(null);
        }
      } else {
        setCurrentIdp(null);
      }

    } catch (err: unknown) {
      console.error('Dashboard error:', err);
      setError(genericCopy.dashboard);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [user?.id, userRolesKey]); // Stable roles key so we don't refetch when context re-renders with same roles

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
    }
  }, [loadDashboardData, user?.id]);

  // Track current cycle filter so we only apply dashboard-reporting responses that still match (avoids race where an older response overwrites filtered data)
  const dashboardCycleFilterRef = useRef(dashboardCycleFilter);
  useEffect(() => {
    dashboardCycleFilterRef.current = dashboardCycleFilter;
  }, [dashboardCycleFilter]);

  // Fetch dashboard reporting when cycle filter or user changes (so changing cycle refetches org stats only)
  const userRolesForReporting = useMemo(() => user?.roles || [], [user?.roles]);
  useEffect(() => {
    if (!user?.id) return;
    if (!userRolesForReporting.some((r: string) => r === 'Administrator' || r === 'SeniorManager' || r === 'TeamLead')) {
      setDashboardReportingLoading(false);
      return;
    }
    const requestId = ++dashboardReportingRequestIdRef.current;
    setDashboardReportingLoading(true);
    const requestedCycleId = dashboardCycleFilter;
    const url = requestedCycleId
      ? `/reviews/dashboard-reporting?cycleId=${encodeURIComponent(requestedCycleId)}`
      : '/reviews/dashboard-reporting';
    apiService
      .get<DashboardReporting>(url)
      .then((res) => {
        if (dashboardReportingRequestIdRef.current !== requestId) return;
        // Only apply if the user hasn't changed the cycle filter since we sent this request (avoids stale response overwriting table)
        if (dashboardCycleFilterRef.current !== requestedCycleId) return;
        setDashboardReporting(res.data);
        setDashboardReportingLoading(false);
      })
      .catch((err: unknown) => {
        if (dashboardReportingRequestIdRef.current !== requestId) return;
        console.error('Failed to load dashboard reporting:', err);
        if (dashboardCycleFilterRef.current !== requestedCycleId) return;
        setDashboardReporting({
          currentCycleName: null,
          daysRemaining: null,
          departmentCount: 0,
          totalPendingCount: 0,
          totalCompletedCount: 0,
          totalExcludedCount: 0,
          pendingReviewsByDepartment: [],
          completedReviewsByDepartment: [],
          excludedReviewsByDepartment: [],
        });
        setDashboardReportingLoading(false);
      });
  }, [user?.id, userRolesForReporting, dashboardCycleFilter]);

  // Fetch my reviews and managed reviews when user or cycle filter changes (scoped to one cycle when filter set = faster load)
  useEffect(() => {
    if (!user?.id) return;
    const requestId = ++reviewsRequestIdRef.current;
    setReviewsLoading(true);
    const cycleParam = dashboardCycleFilter ? `?cycleId=${encodeURIComponent(dashboardCycleFilter)}` : '';
    const myReviewsPromise = apiService
      .get<Review[]>(`/reviews${cycleParam}`)
      .then((res) => {
        if (reviewsRequestIdRef.current !== requestId) return;
        setMyReviews(res.data ?? []);
      })
      .catch((err: unknown) => {
        console.error('Failed to load my reviews:', err);
        if (reviewsRequestIdRef.current !== requestId) return;
        setMyReviews([]);
      });
    let managedReviewsPromise: Promise<void>;
    if (userRolesForReporting.some((r: string) => r === 'Manager' || r === 'SeniorManager' || r === 'Administrator' || r === 'TeamLead')) {
      managedReviewsPromise = apiService
        .get<Review[]>(`/reviews/managed${cycleParam}`)
        .then((res) => {
          if (reviewsRequestIdRef.current !== requestId) return;
          setManagedReviews(res.data ?? []);
        })
        .catch((err: unknown) => {
          console.error('Failed to load managed reviews:', err);
          if (reviewsRequestIdRef.current !== requestId) return;
          setManagedReviews([]);
        });
    } else {
      managedReviewsPromise = Promise.resolve();
      setManagedReviews([]);
    }
    Promise.allSettled([myReviewsPromise, managedReviewsPromise]).finally(() => {
      if (reviewsRequestIdRef.current !== requestId) return;
      setReviewsLoading(false);
    });
  }, [user?.id, userRolesForReporting, dashboardCycleFilter]);

  // When there are active cycles, default to the first (by start date) once so the dashboard focuses on one cycle and completion % is per-cycle
  const hasDefaultedCycleFilter = useRef(false);
  useEffect(() => {
    if (hasDefaultedCycleFilter.current) return;
    const active = (reviewCycles || []).filter((c) => c.isActive);
    if (active.length >= 1) {
      const firstByStart = [...active].sort((a, b) => utcMs(a.startDate) - utcMs(b.startDate))[0];
      if (firstByStart) {
        setDashboardCycleFilter(firstByStart.id);
        hasDefaultedCycleFilter.current = true;
      }
    }
  }, [reviewCycles]);

  // Refetch when user returns to this tab after being away (e.g. another tab for 30+ seconds) so we don't refetch on quick switches
  const TAB_HIDDEN_BEFORE_REFETCH_MS = 30_000;
  useEffect(() => {
    let hiddenAt: number | null = null;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      }
      if (document.visibilityState === 'visible' && user?.id) {
        const hiddenDuration = hiddenAt != null ? Date.now() - hiddenAt : 0;
        if (hiddenDuration >= TAB_HIDDEN_BEFORE_REFETCH_MS) loadDashboardData();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [loadDashboardData, user?.id]);

  useEffect(() => {
    if (createDialogOpen && !useCalendarYear) {
      fiscalYearService.getCurrent().then((r) => setCurrentFiscalYear(r.fiscalYear)).catch(() => setCurrentFiscalYear(null));
    }
  }, [createDialogOpen, useCalendarYear]);

  const handleCreateReviewCycle = async () => {
    if (!createForm.name || !createForm.startDate || !createForm.endDate) {
      setCreateError('Please fill in all required fields');
      return;
    }

    if (createForm.startDate >= createForm.endDate) {
      setCreateError('End date must be after start date');
      return;
    }

    if (!createForm.employeeCutoff) {
      setCreateError('Employee cutoff is required');
      return;
    }
    if (createForm.employeeCutoff > createForm.endDate) {
      setCreateError('Employee cutoff cannot be after end date');
      return;
    }

    try {
      setCreating(true);
      setCreateError('');
      await apiService.post('/review-cycles', {
        name: createForm.name,
        startDate: formatCalendarDateIso(createForm.startDate),
        endDate: formatCalendarDateIso(createForm.endDate),
        employeeCutoff: formatCalendarDateIso(createForm.employeeCutoff),
      });
      setCreateDialogOpen(false);
      setCreateForm({ name: '', startDate: null, endDate: null, employeeCutoff: null });
      setCreateDovetailInfo('');
      loadDashboardData();
    } catch (err: unknown) {
            setCreateError(getApiErrorMessage(err, couldNotCreate('the review cycle')));
    } finally {
      setCreating(false);
    }
  };

  const handleStartDateChange = async (date: Date | null) => {
    setCreateDovetailInfo('');
    setCreateForm({ ...createForm, startDate: date });
    if (date) {
      try {
        const { fiscalYear, quarter } = await fiscalYearService.evaluateDate(date);
        const suggestedName = getSuggestedReviewCycleName(fiscalYear, quarter);
        if (!createForm.name) {
          setCreateForm((prev) => ({ ...prev, startDate: date, name: suggestedName }));
        }
      } catch {
        // Leave name unchanged if API fails
      }
    }
  };

  const handleSuggestFiscalQuarter = async (fiscalYear: number, quarter: number) => {
    try {
      const range = await fiscalYearService.getQuarterRange(fiscalYear, quarter);
      const suggested = buildSuggestedReviewCycleDates(
        parseUtcDate(range.start)!,
        parseUtcDate(range.end)!,
        reviewCycles.map((c) => ({ name: c.name, startDate: c.startDate, endDate: c.endDate }))
      );
      if (!suggested.ok) {
        setCreateError(suggested.message);
        setCreateDovetailInfo('');
        return;
      }
      setCreateError('');
      setCreateDovetailInfo(dovetailAdjustmentMessage(suggested) ?? '');
      setCreateForm({
        name: getSuggestedReviewCycleName(fiscalYear, quarter),
        startDate: suggested.startDate,
        endDate: suggested.endDate,
        employeeCutoff: suggested.employeeCutoff,
      });
    } catch {
      // Leave form unchanged if API fails
    }
  };

  const getFinalRatingLabel = (value: number | string | undefined): string | null => {
    if (value == null) return null;
    if (typeof value === 'number' && (value === 1 || value === 2 || value === 3)) {
      const labels: Record<number, string> = { 1: 'Development Required', 2: 'Meets Expectation', 3: 'Exceeds Expectation' };
      return labels[value] ?? null;
    }
    if (typeof value === 'string') {
      const map: Record<string, string> = {
        DevelopmentRequired: 'Development Required',
        MeetsExpectation: 'Meets Expectation',
        ExceedsExpectation: 'Exceeds Expectation',
      };
      return map[value] ?? null;
    }
    return null;
  };

  // Handle both 'roles' (camelCase) and 'Roles' (PascalCase) for compatibility
  // Memoize these to prevent unnecessary re-renders
  const userRoles = useMemo(() => user?.roles || (user as { Roles?: string[] })?.Roles || [], [user]);
  const isManager = useMemo(() => userRoles.some((r: string) => r === 'Manager' || r === 'SeniorManager' || r === 'Administrator' || r === 'TeamLead'), [userRoles]);
  const isAdmin = useMemo(() => userRoles.includes('Administrator'), [userRoles]);
  const isSeniorManager = useMemo(() => userRoles.includes('SeniorManager'), [userRoles]);
  const canAccessOrganisationDashboard = useMemo(() => isAdmin || isSeniorManager, [isAdmin, isSeniorManager]);
  /** Administrators and Senior Managers can drill down from org dashboard; Senior Managers see only their hierarchy (CEO sees all). */
  const canAccessAllReviews = useMemo(() => isAdmin || isSeniorManager, [isAdmin, isSeniorManager]);
  const isTeamLeadOnly = useMemo(() => userRoles.includes('TeamLead') && !userRoles.some((r: string) => r === 'Manager' || r === 'SeniorManager' || r === 'Administrator'), [userRoles]);
  /** ReportingExempt users (e.g. CEO) are excluded from reviews and IDPs; hide those dashboard cards on My view. */
  const isReportingExempt = useMemo(() => userRoles.includes('ReportingExempt'), [userRoles]);
  const hasTeamMembersNeedingAcknowledgment = teamMembersNeedingAcknowledgment.length > 0;

  // Determine active view from URL params; default to My Dashboard for everyone
  const activeView: DashboardView = (viewType as DashboardView | undefined) || 'my';
  usePageAgentContext({
    pageId: 'dashboard',
    pageTitle: 'Dashboard',
    viewMode: activeView,
    visibleSections: ['reviews', 'check-ins', 'idp', 'leave'],
    suggestedPrompts: [
      'What does each dashboard card mean?',
      'Which actions should I prioritise here?',
      'Explain what this dashboard shows for my role.',
    ],
  });

  // Redirect to My Dashboard when on root or /dashboard with no viewType
  useEffect(() => {
    if ((location.pathname === '/' || location.pathname === '/dashboard') && !viewType) {
      navigate('/dashboard/my', { replace: true });
    }
  }, [location.pathname, viewType, navigate]);

  // Split managed reviews into direct reports (where user is the manager)
  // Must be before any early returns
  const directReports = useMemo(() => {
    if (!user?.id) return [];
    return managedReviews.filter((r) => r.managerId === user.id);
  }, [managedReviews, user?.id]);

  // Get set of direct report employee IDs for filtering check-ins
  const directReportEmployeeIds = useMemo(() => {
    return new Set(directReports.map((r) => r.employeeId));
  }, [directReports]);

  // Filter check-ins to only show direct reports
  const directReportCheckIns = useMemo(() => {
    if (!managerCheckInData) return null;
    const filteredUpcoming = managerCheckInData.upcomingCheckIns.filter((ci) =>
      directReportEmployeeIds.has(ci.employeeId)
    );
    const filteredOverdue = managerCheckInData.overdueCheckIns.filter((ci) =>
      directReportEmployeeIds.has(ci.employeeId)
    );
    const filteredNeedsScheduling = managerCheckInData.needsSchedulingAcknowledgment.filter((ci) =>
      directReportEmployeeIds.has(ci.employeeId)
    );
    const filteredRecentCompleted = managerCheckInData.recentCompletedCheckIns?.filter((ci) =>
      directReportEmployeeIds.has(ci.employeeId)
    ) || [];
    
    // Recalculate awaitingManagerAction count from filtered overdue check-ins
    // (check-ins that are past due and awaiting manager decision)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const filteredAwaitingAction = filteredOverdue.filter((ci) => {
      const dueDate = parseUtcDate(ci.dueDate)!;
      return dueDate < today && ci.status === 'AwaitingManagerDecision';
    }).length;
    
    return {
      ...managerCheckInData,
      upcomingCheckIns: filteredUpcoming,
      overdueCheckIns: filteredOverdue,
      needsSchedulingAcknowledgment: filteredNeedsScheduling,
      recentCompletedCheckIns: filteredRecentCompleted,
      awaitingManagerAction: filteredAwaitingAction,
    };
  }, [managerCheckInData, directReportEmployeeIds]);

  // Single "next" team check-in for managers: overdue first, then needs scheduling, then soonest upcoming
  const nextTeamCheckIn = useMemo(() => {
    if (!directReportCheckIns) return null;
    const byDue = (a: { dueDate: string }, b: { dueDate: string }) =>
      utcMs(a.dueDate) - utcMs(b.dueDate);
    const overdue = [...directReportCheckIns.overdueCheckIns].sort(byDue)[0];
    if (overdue) return overdue;
    const needs = [...directReportCheckIns.needsSchedulingAcknowledgment].sort(byDue)[0];
    if (needs) return needs;
    const upcoming = [...directReportCheckIns.upcomingCheckIns].sort(byDue)[0];
    return upcoming ?? null;
  }, [directReportCheckIns]);

  /** Progress weight for a review (0–1). Same scale everywhere: NotStarted/NotSubmitted 0%, SelfReviewInProgress 25%, SelfReviewComplete 50%, PendingDiscussion/UnderDispute 75%, Finalised/DiscussionAcknowledged/Excluded 100%. */
  const getMyReviewProgressWeight = (r: Review): number => {
    const s = String(r.status).toLowerCase();
    if (s === 'notstarted' || s === 'notsubmitted' || r.status === 1 || r.status === 9) return 0;
    if (s === 'selfreviewinprogress' || r.status === 2) return 0.25;
    if (s === 'selfreviewcomplete' || r.status === 3) return 0.5;
    if (s === 'pendingdiscussion' || s === 'underdispute' || r.status === 4 || r.status === 7) return 0.75;
    if (s === 'discussionacknowledged' || s === 'finalised' || s === 'excluded' || r.status === 5 || r.status === 6 || r.status === 8) return 1;
    return 0;
  };

  // Calculate completion percentage by department (must be before early returns to keep hook order)
  const departmentCompletionRates = useMemo(() => {
    if (!dashboardReporting) return [];
    // Collect all unique department IDs/names from pending, completed, and excluded
    const allDeptKeys = new Set<string>();
    const deptMap = new Map<string, { departmentId?: string; departmentName?: string }>();
    for (const list of [dashboardReporting.pendingReviewsByDepartment, dashboardReporting.completedReviewsByDepartment, dashboardReporting.excludedReviewsByDepartment ?? []]) {
      for (const d of list) {
        const key = d.departmentId || d.departmentName || '';
        allDeptKeys.add(key);
        if (!deptMap.has(key)) deptMap.set(key, { departmentId: d.departmentId, departmentName: d.departmentName });
      }
    }
    return Array.from(allDeptKeys).map((key) => {
      const info = deptMap.get(key)!;
      const pendingDept = dashboardReporting.pendingReviewsByDepartment.find(
        (d) => (d.departmentId || d.departmentName || '') === key
      );
      const completedDept = dashboardReporting.completedReviewsByDepartment.find(
        (d) => (d.departmentId || d.departmentName || '') === key
      );
      const excludedDept = (dashboardReporting.excludedReviewsByDepartment ?? []).find(
        (d) => (d.departmentId || d.departmentName || '') === key
      );
      const pendingCount = pendingDept?.count || 0;
      const completed = completedDept?.count || 0;
      const excluded = excludedDept?.count || 0;
      const total = pendingCount + completed + excluded;
      const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
      return {
        departmentId: info.departmentId,
        departmentName: info.departmentName,
        pending: pendingCount,
        completed: completed,
        excluded: excluded,
        total: total,
        percentage: percentage,
      };
    })
      .sort((a, b) => a.departmentName?.localeCompare(b.departmentName || '') || 0);
  }, [dashboardReporting]);

  // Review Status by Department table: compute counts per dept, then filter and sort (must run unconditionally for Rules of Hooks)
  const deptReviewTableFilteredAndSorted = useMemo(() => {
    const rows = departmentCompletionRates.map((dept) => {
      const pendingDept = dashboardReporting?.pendingReviewsByDepartment.find(d =>
        (d.departmentId && d.departmentId === dept.departmentId) || (d.departmentName && d.departmentName === dept.departmentName)
      );
      const completedDept = dashboardReporting?.completedReviewsByDepartment.find(d =>
        (d.departmentId && d.departmentId === dept.departmentId) || (d.departmentName && d.departmentName === dept.departmentName)
      );
      const excludedDept = dashboardReporting?.excludedReviewsByDepartment?.find(d =>
        (d.departmentId && d.departmentId === dept.departmentId) || (d.departmentName && d.departmentName === dept.departmentName)
      );
      const deptReviews = [
        ...(pendingDept?.reviews || []),
        ...(completedDept?.reviews || []),
        ...(excludedDept?.reviews || []),
      ];
      // When reviews are withheld (e.g. senior manager), use API status counts so the table still matches the summary chip
      const useBackendCounts = deptReviews.length === 0 && dept.total > 0;
      let notStarted: number;
      let inProgress: number;
      let pending: number;
      let disputed: number;
      let complete: number;
      let excluded: number;
      if (useBackendCounts) {
        notStarted = pendingDept?.notStartedCount ?? 0;
        inProgress = pendingDept?.inProgressCount ?? 0;
        pending = pendingDept?.pendingCount ?? 0;
        disputed = pendingDept?.disputedCount ?? 0;
        complete = (pendingDept?.completeCount ?? 0) + (completedDept?.completeCount ?? completedDept?.count ?? 0);
        excluded = excludedDept?.excludedCount ?? excludedDept?.count ?? 0;
      } else {
        notStarted = deptReviews.filter(r => {
          const s = String(r.status).toLowerCase();
          return s === 'notstarted' || s === 'notsubmitted' || r.status === 1 || r.status === 9;
        }).length;
        inProgress = deptReviews.filter(r => {
          const s = String(r.status).toLowerCase();
          return s === 'selfreviewinprogress' || s === 'selfreviewcomplete'
            || r.status === 2 || r.status === 3;
        }).length;
        pending = deptReviews.filter(r => {
          const s = String(r.status).toLowerCase();
          return s === 'pendingdiscussion' || s === 'discussionacknowledged' || r.status === 4 || r.status === 5;
        }).length;
        disputed = deptReviews.filter(r => String(r.status).toLowerCase() === 'underdispute' || r.status === 7).length;
        complete = deptReviews.filter(r => String(r.status).toLowerCase() === 'finalised' || r.status === 6).length;
        excluded = deptReviews.filter(r => String(r.status).toLowerCase() === 'excluded' || r.status === 8).length;
      }
      const total = notStarted + inProgress + pending + disputed + complete + excluded;
      // Use weighted progress (0/25/50/75/100%) when we have review list; fall back to binary when using backend counts only
      const completionPct = useBackendCounts
        ? (total > 0 ? Math.round((complete / total) * 100) : 0)
        : (deptReviews.length > 0
          ? Math.round((deptReviews.reduce((sum, r) => sum + getMyReviewProgressWeight(r), 0) / deptReviews.length) * 100)
          : 0);
      return { dept, notStarted, inProgress, pending, disputed, complete, excluded, total, completionPct };
    });
    const query = deptReviewTableSearch.trim().toLowerCase();
    const filtered = query ? rows.filter(r => (r.dept.departmentName || '').toLowerCase().includes(query)) : rows;
    const sorted = [...filtered].sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;
      switch (deptReviewTableSortBy) {
        case 'departmentName':
          aVal = a.dept.departmentName || '';
          bVal = b.dept.departmentName || '';
          break;
        case 'notStarted': aVal = a.notStarted; bVal = b.notStarted; break;
        case 'inProgress': aVal = a.inProgress; bVal = b.inProgress; break;
        case 'pending': aVal = a.pending; bVal = b.pending; break;
        case 'disputed': aVal = a.disputed; bVal = b.disputed; break;
        case 'excluded': aVal = a.excluded; bVal = b.excluded; break;
        case 'complete': aVal = a.completionPct; bVal = b.completionPct; break; // sort by % for leaderboard
        default: return 0;
      }
      if (aVal < bVal) return deptReviewTableSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return deptReviewTableSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [departmentCompletionRates, dashboardReporting, deptReviewTableSearch, deptReviewTableSortBy, deptReviewTableSortDirection]);

  // IDP Status by Department table: filter and sort (must run unconditionally for Rules of Hooks)
  const idpDeptTableFilteredAndSorted = useMemo(() => {
    const list = idpDashboardReporting?.idpsByDepartment ?? [];
    const rows = list.map((dept) => {
      const expected = dept.expectedEmployeeCount ?? 0;
      const outstandingPct = expected > 0 ? Math.round((dept.outstandingCount / expected) * 100) : 0;
      return {
        dept,
        total: dept.count,
        pending: dept.pendingCount,
        active: dept.activeCount,
        outstanding: dept.outstandingCount,
        notSubmitted: dept.notSubmittedCount,
        expected,
        outstandingPct,
      };
    });
    const query = idpDeptTableSearch.trim().toLowerCase();
    const filtered = query ? rows.filter((r) => (r.dept.departmentName || '').toLowerCase().includes(query)) : rows;
    const sorted = [...filtered].sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;
      switch (idpDeptTableSortBy) {
        case 'departmentName':
          aVal = a.dept.departmentName || '';
          bVal = b.dept.departmentName || '';
          break;
        case 'total': aVal = a.total; bVal = b.total; break;
        case 'pending': aVal = a.pending; bVal = b.pending; break;
        case 'active': aVal = a.active; bVal = b.active; break;
        case 'outstanding': aVal = a.outstandingPct; bVal = b.outstandingPct; break;
        default: return 0;
      }
      if (aVal < bVal) return idpDeptTableSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return idpDeptTableSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [idpDashboardReporting, idpDeptTableSearch, idpDeptTableSortBy, idpDeptTableSortDirection]);

  const orgLeaveTableFiltered = useMemo(() => {
    const list = orgLeaveAtRisk ?? [];
    const query = orgLeaveTableSearch.trim().toLowerCase();
    if (!query) return list;
    return list.filter(
      (r) =>
        (r.employeeName || '').toLowerCase().includes(query) ||
        (r.departmentName || '').toLowerCase().includes(query)
    );
  }, [orgLeaveAtRisk, orgLeaveTableSearch]);

  const orgLeaveTableFilteredAndSorted = useMemo(() => {
    const list = [...orgLeaveTableFiltered];
    const dir = orgLeaveTableSortDirection === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;
      switch (orgLeaveTableSortBy) {
        case 'departmentName':
          aVal = a.departmentName ?? '';
          bVal = b.departmentName ?? '';
          return dir * ((aVal as string).localeCompare(bVal as string));
        case 'employeeName':
          aVal = a.employeeName ?? '';
          bVal = b.employeeName ?? '';
          return dir * ((aVal as string).localeCompare(bVal as string));
        case 'daysTaken':
          aVal = a.annualDaysTaken;
          bVal = b.annualDaysTaken;
          return dir * (aVal - bVal);
        case 'daysToTake':
          aVal = a.daysStillToTake;
          bVal = b.daysStillToTake;
          return dir * (aVal - bVal);
        default:
          return 0;
      }
    });
    return list;
  }, [orgLeaveTableFiltered, orgLeaveTableSortBy, orgLeaveTableSortDirection]);

  // Derived for dashboard (must be before early returns so hooks run unconditionally)
  const activeCycles = reviewCycles.filter((c) => c.isActive);
  const orgLeaveCardsLoading = loading || orgLeaveAtRisk === null;
  const zaLeaveAtRiskCount = (orgLeaveAtRisk ?? []).filter((employee) => employee.countryCode?.toUpperCase() === 'ZA').length;
  const gbLeaveAtRiskCount = (orgLeaveAtRisk ?? []).filter((employee) => employee.countryCode?.toUpperCase() === 'GB').length;
  const inLeaveAtRiskCount = (orgLeaveAtRisk ?? []).filter((employee) => employee.countryCode?.toUpperCase() === 'IN').length;
  const activeCycleIds = new Set(activeCycles.map((c) => c.id));
  /** All cycles for the filter dropdown: active first (by start date), then closed (by end date desc). */
  const allCyclesSorted = useMemo(() => {
    const active = [...activeCycles].sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
    const inactive = reviewCycles.filter((c) => !c.isActive).sort((a, b) => (b.endDate || '').localeCompare(a.endDate || ''));
    return [...active, ...inactive];
  }, [reviewCycles, activeCycles]);
  /** When cycle filter is set, scope to that cycle; otherwise all active cycles. */
  const selectedCycleIds = dashboardCycleFilter
    ? new Set([dashboardCycleFilter])
    : activeCycleIds;
  const myReviewsInScope = selectedCycleIds.size > 0
    ? myReviews.filter((r) => selectedCycleIds.has(r.reviewCycleId))
    : myReviews;
  // Pending = reviews where the employee still has an action (exclude Finalised, Excluded, Discussion Acknowledged)
  const pendingMyReviews = myReviewsInScope.filter((r) => {
    const s = String(r.status).toLowerCase();
    return (
      s !== 'finalised' && r.status !== 6 &&
      s !== 'excluded' && r.status !== 8 &&
      s !== 'discussionacknowledged' && r.status !== 5
    );
  });
  const cycleOrderMap = useMemo(() => new Map(allCyclesSorted.map((c, i) => [c.id, i])), [allCyclesSorted]);
  const selectedCycleForDisplay = (dashboardCycleFilter
    ? reviewCycles.find((c) => c.id === dashboardCycleFilter)
    : activeCycles[0]) ?? activeCycles[0];
  const sortedPendingMyReviews = useMemo(
    () => [...pendingMyReviews].sort((a, b) => (cycleOrderMap.get(a.reviewCycleId) ?? 999) - (cycleOrderMap.get(b.reviewCycleId) ?? 999)),
    [pendingMyReviews, cycleOrderMap]
  );

  if (!user) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
          <Alert severity="error">Could not load your information — try again</Alert>
        </Box>
      </AppLayout>
    );
  }

  /** HR-sourced dates; when absent the Personal milestones section is hidden after load. */
  const showPersonalMilestonesSection = Boolean(
    userDetails?.dateOfBirth || userDetails?.dateOfEngagement
  );

  // Debug logging
  if (reviewCycles.length > 0 && activeCycles.length === 0) {
    console.log('Review cycles loaded but none are active:', reviewCycles.map(c => ({ name: c.name, isActive: c.isActive })));
  }

  // Calculate days remaining for current cycle
  const calculateDaysRemaining = (cycle: ReviewCycle): number | null => {
    if (!cycle.employeeCutoff) return null;
    const cutoff = parseUtcDate(cycle.employeeCutoff)!;
    const now = new Date();
    const diffTime = cutoff.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Filter out disputed reviews from pending lists (they're handled separately by HR)
  const disputedReviews = managedReviews.filter(
    (r) => String(r.status).toLowerCase() === 'underdispute' || r.status === 7
  );
  const disputedCount = disputedReviews.length;

  const pendingDirectReports = directReports.filter(
    (r) => {
      const status = String(r.status).toLowerCase();
      return status !== 'finalised' && status !== 'underdispute' && r.status !== 4 && r.status !== 6 && r.status !== 7;
    }
  );
  const managedReviewsInScope = selectedCycleIds.size > 0
    ? managedReviews.filter((r) => selectedCycleIds.has(r.reviewCycleId))
    : managedReviews;
  // Total people (reviews) in your hierarchy for the selected cycle(s) — used for "Your team and their teams" card
  const totalReportingLineInScopeCount = managedReviewsInScope.length;

  // Completion rates: all use weighted progress (0/25/50/75/100% per stage)
  const myCompletionRate = myReviewsInScope.length > 0
    ? Math.round(
        (myReviewsInScope.reduce((sum, r) => sum + getMyReviewProgressWeight(r), 0) / myReviewsInScope.length) * 100
      )
    : 0;
  const teamCompletionRate =
    managedReviewsInScope.length > 0
      ? Math.round(
          (managedReviewsInScope.reduce((sum, r) => sum + getMyReviewProgressWeight(r), 0) / managedReviewsInScope.length) * 100
        )
      : 0;

  // Organisation completion uses distinct counts so users in multiple departments are not double-counted (green cards)
  const orgTotalPending = dashboardReporting?.totalPendingCount ?? (dashboardReporting?.pendingReviewsByDepartment.reduce((sum, dept) => sum + dept.count, 0) || 0);
  const orgTotalCompleted = dashboardReporting?.totalCompletedCount ?? (dashboardReporting?.completedReviewsByDepartment.reduce((sum, dept) => sum + dept.count, 0) || 0);
  const orgTotalExcluded = dashboardReporting?.totalExcludedCount ?? (dashboardReporting?.excludedReviewsByDepartment?.reduce((sum, dept) => sum + dept.count, 0) || 0);
  const hasOrgCurrentCycleCard = dashboardReportingLoading || !!dashboardReporting?.currentCycleName;
  const orgTotalReviews = orgTotalPending + orgTotalCompleted + orgTotalExcluded;
  const orgCompletionRate =
    dashboardReporting?.orgWeightedCompletionRate != null
      ? Math.round(dashboardReporting.orgWeightedCompletionRate)
      : orgTotalReviews > 0
        ? Math.round((orgTotalCompleted / orgTotalReviews) * 100)
        : 0;

  const handleDeptReviewTableSort = (column: typeof deptReviewTableSortBy) => {
    if (deptReviewTableSortBy === column) {
      setDeptReviewTableSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setDeptReviewTableSortBy(column);
      setDeptReviewTableSortDirection(column === 'departmentName' ? 'asc' : 'desc');
    }
    setDeptReviewTablePage(0);
  };

  const handleIdpDeptTableSort = (column: 'departmentName' | 'total' | 'pending' | 'active' | 'outstanding') => {
    if (idpDeptTableSortBy === column) {
      setIdpDeptTableSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setIdpDeptTableSortBy(column);
      setIdpDeptTableSortDirection(column === 'departmentName' ? 'asc' : 'desc');
    }
    setIdpDeptTablePage(0);
  };

  const handleOrgLeaveTableSort = (column: 'departmentName' | 'employeeName' | 'daysTaken' | 'daysToTake') => {
    if (orgLeaveTableSortBy === column) {
      setOrgLeaveTableSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrgLeaveTableSortBy(column);
      setOrgLeaveTableSortDirection(column === 'departmentName' || column === 'employeeName' ? 'asc' : 'desc');
    }
    setOrgLeaveTablePage(0);
  };

  const getOrgLeaveSortIcon = (column: 'departmentName' | 'employeeName' | 'daysTaken' | 'daysToTake') => {
    if (orgLeaveTableSortBy !== column) return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    return orgLeaveTableSortDirection === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 16 }} />
      : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  const getDeptReviewSortIcon = (column: typeof deptReviewTableSortBy) => {
    if (deptReviewTableSortBy !== column) return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    return deptReviewTableSortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  const getIdpDeptSortIcon = (column: 'departmentName' | 'total' | 'pending' | 'active' | 'outstanding') => {
    if (idpDeptTableSortBy !== column) return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    return idpDeptTableSortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  const getManagerTableSortIcon = (column: typeof managerTableSortBy) => {
    if (managerTableSortBy !== column) return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    return managerTableSortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleTeamReviewsClick = () => {
    navigate(dashboardCycleFilter ? `/team-reviews?cycleId=${encodeURIComponent(dashboardCycleFilter)}` : '/team-reviews');
  };

  const renderLoadingCards = (count: number) => (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {Array.from({ length: count }).map((_, i) => (
        <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ minHeight: 140 }}>
              <Skeleton variant="text" width="45%" height={24} />
              <Skeleton variant="text" width="60%" height={44} sx={{ mt: 1 }} />
              <Skeleton variant="rounded" height={6} sx={{ mt: 2 }} />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderCardNumberSkeleton = (width = 72) => (
    <Skeleton variant="text" width={width} height={52} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
  );

  const renderPersonalMilestonesHeading = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
      <StarsIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
      <Typography variant="h5" fontWeight="700" color="primary">
        Personal milestones
      </Typography>
    </Box>
  );

  /** Matches PersonalMilestoneTiles layout (up to three cards) so the row does not pop in after load. */
  const renderPersonalMilestoneSkeletonRow = () => (
    <Grid container spacing={2}>
      {[0, 1, 2].map((i) => (
        <Grid key={i} size={{ xs: 12, sm: 4 }}>
          <Card variant="outlined" sx={{ height: '100%', borderColor: 'divider' }}>
            <CardContent sx={{ py: 2.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Skeleton variant="rounded" width={36} height={36} sx={{ flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Skeleton variant="text" width="42%" height={18} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="58%" height={40} />
                  <Skeleton variant="text" width="88%" height={20} sx={{ mt: 0.75 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderDashboardLoadingView = (view: DashboardView) => (
    <>
      {view === 'my' && (
        <>
          <ValuesBanner />
          <Box sx={{ mb: 5 }}>
            {renderPersonalMilestonesHeading()}
            {renderPersonalMilestoneSkeletonRow()}
          </Box>
        </>
      )}
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Typography variant="h5" fontWeight="700" color="primary">
            Reviews
          </Typography>
        </Box>
        {renderLoadingCards(view === 'organisation' ? 3 : 4)}
      </Box>
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SchoolIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Typography variant="h5" fontWeight="700" color="primary">
            Individual Development Plans
          </Typography>
        </Box>
        {renderLoadingCards(4)}
      </Box>
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <EventAvailableIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Typography variant="h5" fontWeight="700" color="primary">
            Leave
          </Typography>
        </Box>
        {renderLoadingCards(view === 'organisation' ? 2 : 2)}
      </Box>
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Typography variant="h5" fontWeight="700" color="primary">
            Check-Ins
          </Typography>
        </Box>
        {renderLoadingCards(view === 'organisation' ? 3 : 1)}
      </Box>
    </>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <AppLayout>
        <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Active Review Disputes Alert - Team dashboard only (not org dashboard) */}
          {activeView === 'team' && isManager && disputedCount > 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 3 }}
              icon={<GavelIcon />}
            >
              <Typography variant="body1" fontWeight="600" gutterBottom>
                Active Review Disputes - {disputedCount} review{disputedCount !== 1 ? 's' : ''} under HR resolution
              </Typography>
              <Typography variant="body2">
                {disputedCount} of your team's review{disputedCount !== 1 ? 's have' : ' has'} been disputed and {disputedCount !== 1 ? 'are' : 'is'} currently under HR review. 
                These reviews are locked and cannot be edited or finalised until HR resolves the dispute.
              </Typography>
            </Alert>
          )}

          {/* Team Members Needing Role Template Acknowledgment - Manager Alert */}
          {isManager && hasTeamMembersNeedingAcknowledgment && (
            <Alert
              severity="warning"
              sx={{ mb: 3 }}
              icon={<AssignmentIcon />}
              action={
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  onClick={() => navigate('/team-roster')}
                  sx={{
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 3,
                    py: 1
                  }}
                >
                  Review Team Members ({teamMembersNeedingAcknowledgment.length})
                </Button>
              }
            >
              <Typography variant="body1" fontWeight="600" gutterBottom>
                Action Required: Role Template Changes Pending Your Acknowledgment
              </Typography>
              <Typography variant="body2">
                <strong>{teamMembersNeedingAcknowledgment.length}</strong> team member{teamMembersNeedingAcknowledgment.length !== 1 ? 's' : ''} {teamMembersNeedingAcknowledgment.length === 1 ? 'has' : 'have'} pending role template changes that require your acknowledgment before they can take effect.
              </Typography>
            </Alert>
          )}

          {/* Pending IDP Approvals - Manager Alert */}
          {/* Only show on Team Dashboard view, and only if user has team members */}
          {activeView === 'team' && isManager && teamMembers.length > 0 && pendingIdpApprovalsCount > 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 3 }}
              icon={<AssignmentIcon />}
              action={
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  onClick={() => navigate('/individual-development-plan?pending-approvals=true')}
                  sx={{
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 3,
                    py: 1
                  }}
                >
                  Review IDPs ({pendingIdpApprovalsCount})
                </Button>
              }
            >
              <Typography variant="body1" fontWeight="600" gutterBottom>
                Action Required: Pending IDP Approvals
              </Typography>
              <Typography variant="body2">
                You have <strong>{pendingIdpApprovalsCount}</strong> Individual Development Plan{pendingIdpApprovalsCount !== 1 ? 's' : ''} awaiting your approval. Please review and approve them at your earliest convenience.
              </Typography>
            </Alert>
          )}

          {/* Role Template Change Acknowledgment Notification */}
          {userDetails?.pendingRoleTemplateId && (() => {
            // State comes from API as string 'Approved', but enum is numeric 3
            // Handle both string and enum comparisons
            const stateIsApproved = pendingRoleTemplate?.state === RoleTemplateState.Approved ||
                                   pendingRoleTemplate?.state === 'Approved' ||
                                   pendingRoleTemplate?.state === 3;

            const managerAcknowledged = !!userDetails?.roleTemplateChangeManagerAcknowledgedAt;
            const employeeAcknowledged = !!userDetails?.roleTemplateChangeEmployeeAcknowledgedAt;
            const isManager = user?.roles.includes('Manager') || user?.roles.includes('SeniorManager') || user?.roles.includes('Administrator') || user?.roles.includes('TeamLead');

            // Only show notification if template state is Approved
            if (!stateIsApproved) {
              return null;
            }

            return (
              <>
                {/* Manager/Admin Self-Acknowledgment */}
                {isManager && !managerAcknowledged && !employeeAcknowledged && (
                  <Alert
                    severity="warning"
                    sx={{ mb: 3 }}
                    icon={<AssignmentIcon />}
                    action={
                      <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        onClick={() => navigate('/profile')}
                        sx={{
                          fontWeight: 600,
                          textTransform: 'none',
                          px: 3,
                          py: 1
                        }}
                      >
                        Review & Acknowledge
                      </Button>
                    }
                  >
                    <Typography variant="body1" fontWeight="600" gutterBottom>
                      Role Template Change Requires Your Acknowledgment
                    </Typography>
                    <Typography variant="body2">
                      Your role template has been changed to <strong>{pendingRoleTemplate?.name}</strong> and approved.
                      Please review the template details on your profile page and acknowledge this change to complete the process.
                    </Typography>
                  </Alert>
                )}

                {/* Employee Acknowledgment (after manager has acknowledged) */}
                {managerAcknowledged && !employeeAcknowledged && (
                <Alert
                  severity="warning"
                  sx={{ mb: 3 }}
                  icon={<AssignmentIcon />}
                  action={
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={() => navigate('/profile')}
                      sx={{
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 3,
                        py: 1
                      }}
                    >
                      Review & Acknowledge
                    </Button>
                  }
                >
                  <Typography variant="body1" fontWeight="600" gutterBottom>
                    Role Template Change Requires Your Acknowledgment
                  </Typography>
                  <Typography variant="body2">
                    Your role template is being changed to <strong>{pendingRoleTemplate?.name}</strong> and your manager has acknowledged it.
                    Please review the template details on your profile page and acknowledge this change to complete the process.
                  </Typography>
                </Alert>
                )}
              </>
            );
          })()}

          {/* My Dashboard View – order: WHAT WE VALUE, action alerts, Personal milestones, Reviews, IDP, Leave, Check-ins */}
          {activeView === 'my' && (
            loading ? renderDashboardLoadingView('my') : (
            <>
              {/* WHAT WE VALUE – first; ValuesBanner spacing is tight at top (see component) */}
              <ValuesBanner />
              {/* Outstanding tasks – above Personal milestones */}
              {outstandingTasksCount > 0 && (
                <Alert
                  severity="warning"
                  sx={{ mb: 3 }}
                  icon={<TaskAltIcon />}
                  action={
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={() => navigate('/my-tasks')}
                      sx={{
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 3,
                        py: 1
                      }}
                    >
                      View my tasks
                    </Button>
                  }
                >
                  <Typography variant="body1" fontWeight="600" gutterBottom>
                    You have outstanding tasks
                  </Typography>
                  <Typography variant="body2">
                    You have <strong>{outstandingTasksCount}</strong> task{outstandingTasksCount !== 1 ? 's' : ''} waiting (e.g. self-review, manager review, peer feedback, check-ins). View and complete them to stay on track.
                  </Typography>
                </Alert>
              )}
              {/* Open review disputes – admins only; matches sidebar Disputes badge */}
              {isAdmin && openDisputesCount > 0 && (
                <Alert
                  severity="warning"
                  sx={{ mb: 3 }}
                  icon={<GavelIcon />}
                  action={
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={() => navigate('/disputes')}
                      sx={{
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 3,
                        py: 1,
                      }}
                    >
                      Manage disputes
                    </Button>
                  }
                >
                  <Typography variant="body1" fontWeight="600" gutterBottom>
                    Review disputes need attention
                  </Typography>
                  <Typography variant="body2">
                    There {openDisputesCount === 1 ? 'is' : 'are'}{' '}
                    <strong>{openDisputesCount}</strong> open dispute{openDisputesCount !== 1 ? 's' : ''}. Open Dispute Management to resolve or document them.
                  </Typography>
                </Alert>
              )}
              {showPersonalMilestonesSection && (
                <Box sx={{ mb: 5 }}>
                  {renderPersonalMilestonesHeading()}
                  <PersonalMilestoneTiles
                    dateOfBirth={userDetails?.dateOfBirth}
                    dateOfEngagement={userDetails?.dateOfEngagement}
                    companyName={userDetails?.companyName}
                  />
                </Box>
              )}
              {/* Reviews Section */}
              <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                  <Typography variant="h5" fontWeight="700" color="primary">
                    Reviews
                  </Typography>
                </Box>
                {!isReportingExempt && (
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Card
                        onClick={() => navigate('/review-cycles')}
                        sx={{
                          cursor: 'pointer',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)' },
                        }}
                      >
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                              Current Cycle
                            </Typography>
                            {selectedCycleForDisplay ? (
                              <>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, lineHeight: 1.2 }}>
                                  {selectedCycleForDisplay.name}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mb: 0.5 }}>
                                  {formatDateRange(selectedCycleForDisplay.startDate, selectedCycleForDisplay.endDate)}
                                </Typography>
                                {selectedCycleForDisplay.employeeCutoff && (
                                  <>
                                    <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mb: 0.5 }}>
                                      <strong>Employee cutoff:</strong> {formatDate(selectedCycleForDisplay.employeeCutoff)}
                                    </Typography>
                                    {(() => {
                                      const daysRemaining = calculateDaysRemaining(selectedCycleForDisplay);
                                      if (daysRemaining !== null) {
                                        return (
                                          <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }}>
                                            {daysRemaining > 0
                                              ? `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} until cutoff`
                                              : daysRemaining === 0
                                              ? 'Cutoff is today'
                                              : `${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) !== 1 ? 's' : ''} past cutoff`}
                                          </Typography>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </>
                                )}
                              </>
                            ) : (
                              <Typography variant="body1" sx={{ opacity: 0.85 }}>
                                No active cycle
                              </Typography>
                            )}
                          </Box>
                          <CalendarTodayIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end', mt: 1 }} />
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Card
                        sx={{
                          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                          color: 'white',
                          boxShadow: '0 8px 24px rgba(67, 233, 123, 0.3)',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(67, 233, 123, 0.4)' },
                        }}
                      >
                        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 140 }}>
                          <Box sx={{ flex: 1 }}>
                            <Tooltip title="Progress by stage: Not started 0%, In progress 25%, Self-review submitted 50%, Awaiting discussion/finalisation 75%, Finalised 100%." placement="top">
                              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                Completion Rate
                              </Typography>
                            </Tooltip>
                            {reviewsLoading ? (
                              <>
                                <Skeleton variant="text" width={80} height={52} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                                <Skeleton variant="rounded" height={6} sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.4)' }} />
                              </>
                            ) : (
                              <>
                                <Typography variant="h3" fontWeight="bold">
                                  {myCompletionRate}%
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={myCompletionRate}
                                  sx={{
                                    mt: 2,
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                    '& .MuiLinearProgress-bar': { bgcolor: 'white' },
                                  }}
                                />
                              </>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
                {/* Current review status panel - hidden for ReportingExempt */}
                {!isReportingExempt && (
                  <Card
                    elevation={0}
                    sx={{
                      background: 'linear-gradient(145deg, rgba(67, 233, 123, 0.08) 0%, rgba(56, 249, 215, 0.06) 100%)',
                      boxShadow: '0 4px 20px rgba(67, 233, 123, 0.12)',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'rgba(67, 233, 123, 0.25)',
                      transition: 'box-shadow 0.2s',
                      '&:hover': { boxShadow: '0 8px 30px rgba(67, 233, 123, 0.18)' },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                              borderRadius: 2,
                              p: 1,
                              mr: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PersonIcon sx={{ color: 'white' }} />
                          </Box>
                          <Typography variant="h6" fontWeight="600">
                            Current Review Status
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigate('/my-reviews')}
                          sx={{ textTransform: 'none' }}
                        >
                          View All{pendingMyReviews.length > 0 ? ` (${pendingMyReviews.length})` : ''}
                        </Button>
                      </Box>
                      {pendingMyReviews.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                          <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            No pending reviews
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {sortedPendingMyReviews.length > 5 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Showing top 5 of {sortedPendingMyReviews.length} pending reviews
                            </Typography>
                          )}
                          {sortedPendingMyReviews.slice(0, 5).map((review) => (
                            <Paper
                              key={review.id}
                              onClick={() => navigate(`/reviews/${review.id}`)}
                              sx={{
                                p: 2,
                                bgcolor: 'grey.50',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  bgcolor: 'info.50',
                                  borderColor: 'info.200',
                                  transform: 'translateX(4px)',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                                    {review.reviewCycleName || 'Review'}
                                  </Typography>
                                  <Chip
                                    label={formatReviewStatus(review.status)}
                                    size="small"
                                    color={getReviewStatusColor(review.status)}
                                    sx={{ mt: 0.5 }}
                                  />
                                  {(review.combinedRating != null || getFinalRatingLabel(review.finalRating)) && (
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                      {review.combinedRating != null && `${review.combinedRating.toFixed(1)}`}
                                      {review.combinedRating != null && getFinalRatingLabel(review.finalRating) && ' · '}
                                      {getFinalRatingLabel(review.finalRating) || ''}
                                    </Typography>
                                  )}
                                </Box>
                                {(review.status === 'NotStarted' ||
                                  review.status === 0 ||
                                  review.status === 'SelfReviewInProgress' ||
                                  review.status === 1) ? (
                                  <PendingIcon color="warning" sx={{ ml: 2 }} />
                                ) : (
                                  <CheckCircleIcon color="success" sx={{ ml: 2 }} />
                                )}
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}
                {/* Reporting-exempt users still see the Current Cycle card */}
                {isReportingExempt && (
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Card
                        onClick={() => navigate('/review-cycles')}
                        sx={{
                          cursor: 'pointer',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)' },
                        }}
                      >
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                              Current Cycle
                            </Typography>
                            {selectedCycleForDisplay ? (
                              <>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, lineHeight: 1.2 }}>
                                  {selectedCycleForDisplay.name}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mb: 0.5 }}>
                                  {formatDateRange(selectedCycleForDisplay.startDate, selectedCycleForDisplay.endDate)}
                                </Typography>
                                {selectedCycleForDisplay.employeeCutoff && (
                                  <>
                                    <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mb: 0.5 }}>
                                      <strong>Employee cutoff:</strong> {formatDate(selectedCycleForDisplay.employeeCutoff)}
                                    </Typography>
                                    {(() => {
                                      const daysRemaining = calculateDaysRemaining(selectedCycleForDisplay);
                                      if (daysRemaining !== null) {
                                        return (
                                          <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }}>
                                            {daysRemaining > 0
                                              ? `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} until cutoff`
                                              : daysRemaining === 0
                                              ? 'Cutoff is today'
                                              : `${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) !== 1 ? 's' : ''} past cutoff`}
                                          </Typography>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </>
                                )}
                              </>
                            ) : (
                              <Typography variant="body1" sx={{ opacity: 0.85 }}>
                                No active cycle
                              </Typography>
                            )}
                          </Box>
                          <CalendarTodayIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end', mt: 1 }} />
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
              </Box>

              {/* Individual Development Plans Section - hidden for ReportingExempt */}
              {!isReportingExempt && (
                <Box sx={{ mb: 5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <SchoolIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                    <Typography variant="h5" fontWeight="700" color="primary">
                      Individual Development Plans
                    </Typography>
                  </Box>
                  <Card
                    elevation={0}
                    sx={{
                      background: 'linear-gradient(145deg, rgba(255, 167, 38, 0.08) 0%, rgba(255, 112, 67, 0.06) 100%)',
                      boxShadow: '0 4px 20px rgba(255, 167, 38, 0.12)',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'rgba(255, 167, 38, 0.25)',
                      transition: 'box-shadow 0.2s',
                      '&:hover': { boxShadow: '0 8px 30px rgba(255, 167, 38, 0.18)' },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Box
                          sx={{
                            background: 'linear-gradient(135deg, #ff9800 0%, #ff7043 100%)',
                            borderRadius: 2,
                            p: 1,
                            mr: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <SchoolIcon sx={{ color: 'white' }} />
                        </Box>
                        <Typography variant="h6" fontWeight="600">
                          Development Plan
                        </Typography>
                      </Box>
                      {currentIdp ? (
                        <Paper
                          onClick={() => navigate(`/individual-development-plan?year=${currentIdp.year}`)}
                          sx={{
                            p: 2.5,
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            boxShadow: 1,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: 'primary.50',
                              transform: 'translateX(4px)',
                              boxShadow: 3,
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {useCalendarYear ? 'Calendar' : 'Fiscal'} Year {currentIdp.year}
                            </Typography>
                            <Chip
                              label={(() => {
                                const status = currentIdp.status;
                                if (status === IdpStatus.Draft || status === 1 || status === 'Draft') return 'Draft';
                                if (status === IdpStatus.WithManager || status === 2 || status === 'WithManager') return 'With manager';
                                if (status === IdpStatus.Acknowledged || status === 3 || status === 'Acknowledged') return 'Acknowledged';
                                if (status === IdpStatus.Completed || status === 4 || status === 'Completed') return 'Completed';
                                return 'Unknown';
                              })()}
                              size="small"
                              color={(() => {
                                const status = currentIdp.status;
                                if (status === IdpStatus.Draft || status === 1 || status === 'Draft') return 'default';
                                if (status === IdpStatus.WithManager || status === 2 || status === 'WithManager') return 'warning';
                                if (status === IdpStatus.Acknowledged || status === 3 || status === 'Acknowledged') return 'success';
                                if (status === IdpStatus.Completed || status === 4 || status === 'Completed') return 'info';
                                return 'default';
                              })()}
                            />
                          </Box>
                          {currentIdp.developmentGoals.length > 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {currentIdp.developmentGoals.length} development goal{currentIdp.developmentGoals.length !== 1 ? 's' : ''}
                            </Typography>
                          )}
                        </Paper>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <SchoolIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            No Development Plan for {useCalendarYear ? 'Calendar' : 'Fiscal'} Year {useCalendarYear ? new Date().getFullYear() : (currentFiscalYear ?? '…')}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Leave Section – one card per leave type */}
              <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <EventAvailableIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                  <Typography variant="h5" fontWeight="700" color="primary">
                    Leave
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  {(() => {
                    const withDays = leaveTypes
                      .map((type) => ({ type, days: myLeaveDaysByType[type.name] ?? myLeaveDaysByType[type.code] ?? 0 }))
                      .filter(({ days }) => days > 0);
                    if (withDays.length === 0) {
                      return (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Card
                        onClick={() => navigate('/leave/me')}
                        sx={{
                          cursor: 'pointer',
                          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                          color: 'white',
                          boxShadow: '0 8px 24px rgba(17, 153, 142, 0.3)',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(17, 153, 142, 0.4)' },
                        }}
                      >
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 110 }}>
                          <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                            Leave this year
                          </Typography>
                          {loading ? (
                            renderCardNumberSkeleton(84)
                          ) : (
                            <Typography variant="h5" fontWeight="bold">
                              {myLeaveBurndown !== null ? myLeaveBurndown.annualDaysTaken : 0} days
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ opacity: 0.85, mt: 1 }}>
                            View my leave →
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                    }
                    const gradients = [
                      'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                      'linear-gradient(135deg, #4776E6 0%, #8E54e9 100%)',
                      'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                      'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)',
                      'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                      'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
                    ];
                    return withDays.map(({ type, days }, idx) => {
                      const bg = gradients[idx % gradients.length];
                      return (
                        <Grid key={type.id} size={{ xs: 12, sm: 6, md: 4 }}>
                          <Card
                            onClick={() => navigate('/leave/me')}
                            sx={{
                              cursor: 'pointer',
                              background: bg,
                              color: 'white',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                              transition: 'transform 0.2s, box-shadow 0.2s',
                              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(0,0,0,0.18)' },
                            }}
                          >
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 110 }}>
                              <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                                {type.name}
                              </Typography>
                              <Typography variant="h5" fontWeight="bold">
                                {days} day{days !== 1 ? 's' : ''}
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.85, mt: 1 }}>
                                View my leave →
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    });
                  })()}
                </Grid>
              </Box>

              {/* Check-Ins Section – last on My Dashboard */}
              <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <CheckCircleIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                  <Typography variant="h5" fontWeight="700" color="primary">
                    Check-Ins
                  </Typography>
                </Box>
                {employeeCheckInData?.managerOptedOutOfBuiltInCheckIns ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {employeeManagerOptedOutShortLabel}
                    </Typography>
                    <Typography variant="body2">
                      {employeeManagerOptedOutMessage}
                    </Typography>
                  </Alert>
                ) : (
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                      onClick={() => navigate('/checkins?view=my')}
                      sx={{
                        cursor: 'pointer',
                        background: 'linear-gradient(135deg, #0288d1 0%, #00bcd4 100%)',
                        color: 'white',
                        boxShadow: '0 8px 24px rgba(2, 136, 209, 0.3)',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(2, 136, 209, 0.4)' },
                      }}
                    >
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                            Next check-in
                          </Typography>
                          {employeeCheckInData?.nextCheckIn ? (
                            <>
                              <Typography variant="body1" fontWeight="600" sx={{ lineHeight: 1.3 }}>
                                With {employeeCheckInData.nextCheckIn.managerName}
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mt: 0.5 }}>
                                Due {formatDate(employeeCheckInData.nextCheckIn.dueDate)}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                              No upcoming check-in
                            </Typography>
                          )}
                        </Box>
                        <CalendarTodayIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end', mt: 1 }} />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                )}
              </Box>
            </>
            )
          )}

          {/* Organisation Dashboard View */}
          {activeView === 'organisation' && canAccessOrganisationDashboard && (
            loading ? renderDashboardLoadingView('organisation') : (
              <>
              {/* Reviews Section */}
              <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                  <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  <Typography variant="h5" fontWeight="700" color="primary">
                    Reviews
                  </Typography>
                  {reviewCycles.length > 0 && (
                    <FormControl size="small" sx={{ minWidth: 260, ml: 'auto' }}>
                      <InputLabel shrink id="dashboard-cycle-filter-label">Review cycle</InputLabel>
                      <Select
                        labelId="dashboard-cycle-filter-label"
                        id="dashboard-cycle-filter"
                        value={dashboardCycleFilter}
                        label="Review cycle"
                        onChange={(e) => setDashboardCycleFilter(e.target.value)}
                        displayEmpty
                        notched
                        renderValue={(value) => {
                          if (value === '') return activeCycles.length > 0 ? `All active cycles (${activeCycles.length})` : 'Select a cycle';
                          const cycle = allCyclesSorted.find((c) => c.id === value);
                          if (!cycle) return value;
                          const range = cycle.startDate && cycle.endDate ? ` (${formatDateRange(cycle.startDate, cycle.endDate)})` : '';
                          return cycle.isActive ? `${cycle.name}${range}` : `${cycle.name}${range} (Closed)`;
                        }}
                      >
                        {activeCycles.length > 0 && (
                          <MenuItem value="">
                            All active cycles ({activeCycles.length})
                          </MenuItem>
                        )}
                        {allCyclesSorted.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                            {c.startDate && c.endDate && ` (${formatDateRange(c.startDate, c.endDate)})`}
                            {!c.isActive && ' (Closed)'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
                
                <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Current Cycle */}
                {hasOrgCurrentCycleCard && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Card
                      sx={{
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        height: '100%',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)' },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                              Current Cycle
                            </Typography>
                            {dashboardReportingLoading ? (
                              <>
                                <Skeleton variant="text" width={180} height={38} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                                <Skeleton variant="text" width={140} height={22} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                              </>
                            ) : (
                              <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
                                {dashboardReporting?.currentCycleName}
                              </Typography>
                            )}
                            {!dashboardReportingLoading && dashboardReporting?.daysRemaining !== null && dashboardReporting?.daysRemaining !== undefined && (
                              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                {dashboardReporting.daysRemaining > 0
                                  ? `${dashboardReporting.daysRemaining} days remaining`
                                  : dashboardReporting.daysRemaining === 0
                                  ? 'Cycle ends today'
                                  : 'Cycle has ended'}
                              </Typography>
                            )}
                            {isAdmin && !dashboardReportingLoading && dashboardReporting?.currentCycleId && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => navigate(`/review-cycles/${dashboardReporting.currentCycleId}/outstanding`)}
                                sx={{
                                  mt: 1.5,
                                  color: 'white',
                                  borderColor: 'rgba(255,255,255,0.7)',
                                  textTransform: 'none',
                                  '&:hover': {
                                    borderColor: 'white',
                                    bgcolor: 'rgba(255,255,255,0.12)',
                                  },
                                }}
                              >
                                View outstanding tasks
                              </Button>
                            )}
                          </Box>
                          <CalendarTodayIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Organisation Completion Rate */}
                <Grid size={{ xs: 12, md: hasOrgCurrentCycleCard ? 4 : 6 }}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(67, 233, 123, 0.3)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(67, 233, 123, 0.4)' },
                      height: '100%',
                    }}
                  >
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140, p: 3 }}>
                      <Box>
                        <Tooltip title="Progress by stage: Not started 0%, In progress 25%, Self-review submitted 50%, Awaiting discussion/finalisation 75%, Finalised 100%. Company average across all reviews." placement="top">
                          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                            Completion Rate
                          </Typography>
                        </Tooltip>
                        {dashboardReportingLoading ? (
                          <>
                            <Skeleton variant="text" width={80} height={52} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                            <Skeleton variant="rounded" height={6} sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.4)' }} />
                          </>
                        ) : (
                          <>
                            <Typography variant="h3" fontWeight="bold">
                              {orgCompletionRate}%
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={orgCompletionRate}
                              sx={{
                                mt: 2,
                                height: 6,
                                borderRadius: 3,
                                bgcolor: 'rgba(255,255,255,0.3)',
                                '& .MuiLinearProgress-bar': { bgcolor: 'white' },
                              }}
                            />
                          </>
                        )}
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" sx={{ opacity: 0.85 }}>
                          {(dashboardReporting?.departmentCount ?? departmentCompletionRates.length)} department{(dashboardReporting?.departmentCount ?? departmentCompletionRates.length) !== 1 ? 's' : ''} tracked
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Total Pending Reviews */}
                <Grid size={{ xs: 12, md: hasOrgCurrentCycleCard ? 4 : 6 }}>
                  <Card
                    onClick={canAccessAllReviews ? () => navigate('/reviews/all?status=pending') : undefined}
                    sx={{
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: orgTotalPending > 0 ? '0 12px 32px rgba(255, 167, 38, 0.4)' : '0 12px 32px rgba(67, 233, 123, 0.4)' },
                      height: '100%',
                      background: orgTotalPending > 0 
                        ? 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)'
                        : 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                      color: 'white',
                      boxShadow: orgTotalPending > 0
                        ? '0 8px 24px rgba(255, 167, 38, 0.3)'
                        : '0 8px 24px rgba(67, 233, 123, 0.3)',
                      cursor: canAccessAllReviews ? 'pointer' : 'default',
                    }}
                  >
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                          Pending Reviews
                        </Typography>
                        {dashboardReportingLoading ? (
                          <>
                            <Skeleton variant="text" width={70} height={52} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                            <Skeleton variant="text" width={110} height={22} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                          </>
                        ) : (
                          <>
                            <Typography variant="h3" fontWeight="bold">
                              {orgTotalPending}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.85, mt: 1 }}>
                              Requiring action
                            </Typography>
                          </>
                        )}
                      </Box>
                      <PendingIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end' }} />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Active Disputes Card - visible to Admins and SeniorManagers (so they know there are disputes); only Admins can open Dispute Management */}
                {(() => {
                  // Calculate dispute counts from all reviews in dashboardReporting
                  const allReviews = [
                    ...(dashboardReporting?.pendingReviewsByDepartment.flatMap(d => d.reviews) || []),
                    ...(dashboardReporting?.completedReviewsByDepartment.flatMap(d => d.reviews) || [])
                  ];
                  const disputedReviews = allReviews.filter(
                    (r) => String(r.status).toLowerCase() === 'underdispute' || r.status === 7
                  );
                  const disputeCount = disputedReviews.length;

                  if (disputeCount === 0) return null;

                  // Find oldest dispute
                  const oldestDispute = disputedReviews
                    .filter(r => r.disputedAt)
                    .sort((a, b) => utcMs(a.disputedAt) - utcMs(b.disputedAt))[0];
                  
                  const oldestDisputeAge = oldestDispute?.disputedAt 
                    ? Math.floor((new Date().getTime() - utcMs(oldestDispute.disputedAt)) / (1000 * 60 * 60 * 24))
                    : null;

                  // Determine color: Amber (1-2), Red (3+ or >14 days old)
                  const getDisputeColor = () => {
                    if (disputeCount <= 2 && (!oldestDisputeAge || oldestDisputeAge <= 14)) return 'warning';
                    return 'error';
                  };

                  const disputeColor = getDisputeColor();
                  const canOpenDisputes = isAdmin;

                  const card = (
                      <Card
                        onClick={canOpenDisputes ? () => navigate('/disputes') : undefined}
                        sx={{
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': { transform: 'translateY(-4px)', boxShadow: disputeColor === 'error' ? '0 12px 32px rgba(245, 87, 108, 0.4)' : disputeColor === 'warning' ? '0 12px 32px rgba(255, 167, 38, 0.4)' : '0 12px 32px rgba(67, 233, 123, 0.4)' },
                          cursor: canOpenDisputes ? 'pointer' : 'default',
                          height: '100%',
                          background: disputeColor === 'error'
                            ? 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)'
                            : disputeColor === 'warning'
                            ? 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)'
                            : 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                          color: 'white',
                          boxShadow: disputeColor === 'error'
                            ? '0 8px 24px rgba(245, 87, 108, 0.3)'
                            : disputeColor === 'warning'
                            ? '0 8px 24px rgba(255, 167, 38, 0.3)'
                            : '0 8px 24px rgba(67, 233, 123, 0.3)',
                        }}
                      >
                        <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                              Active Disputes
                            </Typography>
                            <Typography variant="h3" fontWeight="bold">
                              {disputeCount}
                            </Typography>
                            {oldestDisputeAge !== null && disputeCount > 0 && (
                              <Typography variant="body2" sx={{ opacity: 0.85, mt: 1 }}>
                                Oldest: {oldestDisputeAge} day{oldestDisputeAge !== 1 ? 's' : ''} old
                              </Typography>
                            )}
                          </Box>
                          <GavelIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end' }} />
                        </CardContent>
                      </Card>
                  );

                  return (
                    <Grid size={{ xs: 12, md: dashboardReporting?.currentCycleName ? 4 : 6 }} key="active-disputes">
                      {canOpenDisputes ? card : (
                        <Tooltip title="Only Administrators can open Dispute Management." placement="top">
                          <Box sx={{ height: '100%' }}>{card}</Box>
                        </Tooltip>
                      )}
                    </Grid>
                  );
                })()}

              {/* Managers that might need coaching (from feedback attestation in finalised reviews; manager never sees what employee said) */}
              {(dashboardReporting?.managersNeedingCoaching?.length ?? 0) > 0 && (
                <Grid size={{ xs: 12, md: dashboardReporting?.currentCycleName ? 4 : 6 }}>
                  <Card
                    sx={{
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(156, 39, 176, 0.25)' },
                      height: '100%',
                      background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(156, 39, 176, 0.3)',
                    }}
                  >
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                          Managers that might need coaching
                        </Typography>
                        <Typography variant="h3" fontWeight="bold">
                          {dashboardReporting!.managersNeedingCoaching!.length}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.85, mt: 1 }}>
                          Direct report{dashboardReporting!.managersNeedingCoaching!.some((m) => m.count > 1) ? 's' : ''} indicated limited or little feedback
                        </Typography>
                      </Box>
                      <SchoolIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end' }} />
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {(adminCheckInData?.managerSummaries?.length ?? 0) > 0 && (
                <Grid size={{ xs: 12, md: dashboardReporting?.currentCycleName ? 4 : 6 }}>
                  <Card
                    onClick={() => {
                      setManagerReviewLoadExpanded(true);
                      setTimeout(() => scrollToSection(managerReviewLoadSectionRef), 0);
                    }}
                    sx={{
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: managerReviewLoadSummary.red > 0 ? '0 12px 32px rgba(245, 87, 108, 0.35)' : managerReviewLoadSummary.amber > 0 ? '0 12px 32px rgba(255, 167, 38, 0.35)' : '0 12px 32px rgba(67, 233, 123, 0.35)' },
                      height: '100%',
                      background: managerReviewLoadSummary.red > 0
                        ? 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)'
                        : managerReviewLoadSummary.amber > 0
                        ? 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)'
                        : 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                      color: 'white',
                      boxShadow: managerReviewLoadSummary.red > 0
                        ? '0 8px 24px rgba(245, 87, 108, 0.3)'
                        : managerReviewLoadSummary.amber > 0
                        ? '0 8px 24px rgba(255, 167, 38, 0.3)'
                        : '0 8px 24px rgba(67, 233, 123, 0.3)',
                    }}
                  >
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                          Managers over review load threshold
                        </Typography>
                        <Typography variant="h3" fontWeight="bold">
                          {managerReviewLoadSummary.overThreshold}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.85, mt: 1 }}>
                          {managerReviewLoadSummary.red} red, {managerReviewLoadSummary.amber} amber
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.85, mt: 1, display: 'block' }}>
                          Click to view affected managers
                        </Typography>
                      </Box>
                      <PeopleIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end' }} />
                    </CardContent>
                  </Card>
                </Grid>
              )}
              </Grid>

              {/* Reviews by Department */}
              <Grid size={{ xs: 12 }}>
                <Accordion
                  defaultExpanded={false}
                  sx={{
                    borderRadius: 1,
                    overflow: 'hidden',
                    background: 'linear-gradient(145deg, rgba(102, 126, 234, 0.04) 0%, rgba(118, 75, 162, 0.03) 100%)',
                    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.1)',
                    border: '1px solid',
                    borderColor: 'rgba(102, 126, 234, 0.12)',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: '0 8px 30px rgba(102, 126, 234, 0.15)' },
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { margin: 0 },
                    '& .MuiAccordionSummary-root': {
                      borderRadius: 2,
                      px: 3,
                      py: 2.5,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      '& .MuiAccordionSummary-content': { alignItems: 'center', my: 0 },
                      '& .MuiAccordionSummary-expandIconWrapper': { ml: 2 },
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <Box
                        sx={{
                          bgcolor: 'primary.main',
                          borderRadius: '50%',
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        <ExpandMoreIcon />
                      </Box>
                    }
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box
                        sx={{
                          bgcolor: 'primary.main',
                          borderRadius: 2,
                          p: 1,
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <AssessmentIcon sx={{ color: 'white' }} />
                      </Box>
                      <Typography variant="h6" fontWeight="600">
                        Status by Department
                      </Typography>
                      <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip
                          label={`${dashboardReporting?.departmentCount ?? departmentCompletionRates.length} department${(dashboardReporting?.departmentCount ?? departmentCompletionRates.length) !== 1 ? 's' : ''}`}
                          size="small"
                          variant="outlined"
                        />
                        {orgTotalPending > 0 && (
                          <Chip
                            label={`${orgTotalPending} pending`}
                            color="warning"
                            size="small"
                          />
                        )}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3, pb: 3 }}>
                    {departmentCompletionRates.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <TrendingUpIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          No department data available
                        </Typography>
                      </Box>
                    ) : (
                      <>
                      <TextField
                        size="small"
                        placeholder="Search by department name..."
                        value={deptReviewTableSearch}
                        onChange={(e) => {
                          setDeptReviewTableSearch(e.target.value);
                          setDeptReviewTablePage(0);
                        }}
                        sx={{ mb: 2, maxWidth: 320 }}
                        fullWidth
                      />
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <Box
                                  component="span"
                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
                                  onClick={() => handleDeptReviewTableSort('departmentName')}
                                >
                                  <strong>Department</strong>
                                  {getDeptReviewSortIcon('departmentName')}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Box
                                  component="span"
                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
                                  onClick={() => handleDeptReviewTableSort('notStarted')}
                                >
                                  <strong>Not Started</strong>
                                  {getDeptReviewSortIcon('notStarted')}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Box
                                  component="span"
                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
                                  onClick={() => handleDeptReviewTableSort('inProgress')}
                                >
                                  <strong>In Progress</strong>
                                  {getDeptReviewSortIcon('inProgress')}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Box
                                  component="span"
                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
                                  onClick={() => handleDeptReviewTableSort('pending')}
                                >
                                  <strong>Pending</strong>
                                  {getDeptReviewSortIcon('pending')}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Box
                                  component="span"
                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
                                  onClick={() => handleDeptReviewTableSort('disputed')}
                                >
                                  <strong>Disputed</strong>
                                  {getDeptReviewSortIcon('disputed')}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Box
                                  component="span"
                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
                                  onClick={() => handleDeptReviewTableSort('excluded')}
                                >
                                  <strong>Excluded</strong>
                                  {getDeptReviewSortIcon('excluded')}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Box
                                  component="span"
                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
                                  onClick={() => handleDeptReviewTableSort('complete')}
                                >
                                  <strong>Complete</strong>
                                  {getDeptReviewSortIcon('complete')}
                                </Box>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {deptReviewTableFilteredAndSorted
                              .slice(
                                deptReviewTablePage * deptReviewTableRowsPerPage,
                                deptReviewTablePage * deptReviewTableRowsPerPage + deptReviewTableRowsPerPage
                              )
                              .map(({ dept, notStarted, inProgress, pending, disputed, complete, excluded, total, completionPct }) => {
                                // RAG for completion: green ≥80%, amber ≥25%, red <25%, grey if no reviews
                                const ragStatus = total === 0 ? 'grey' as const
                                  : completionPct >= 80 ? 'green' as const
                                  : completionPct >= 25 ? 'amber' as const
                                  : 'red' as const;
                                const ragColor = ragStatus === 'green' ? 'success.main' : ragStatus === 'amber' ? 'warning.main' : ragStatus === 'red' ? 'error.main' : 'grey.400';
                                return (
                                <TableRow
                                  key={dept.departmentId || dept.departmentName || 'no-dept'}
                                  sx={{ cursor: canAccessAllReviews ? 'pointer' : 'default', '&:hover': canAccessAllReviews ? { bgcolor: 'action.hover' } : {} }}
                                  onClick={canAccessAllReviews ? () => {
                                    const params = new URLSearchParams();
                                    if (dept.departmentId) params.set('departmentId', dept.departmentId);
                                    if (dept.departmentName) params.set('departmentName', dept.departmentName);
                                    navigate(`/reviews/all?${params.toString()}`);
                                  } : undefined}
                                >
                                  <TableCell>{dept.departmentName}</TableCell>
                                  <TableCell align="right">{notStarted}</TableCell>
                                  <TableCell align="right">{inProgress}</TableCell>
                                  <TableCell align="right">{pending}</TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      color: disputed > 0 ? (disputed >= 3 ? 'error.main' : 'warning.main') : 'inherit',
                                      fontWeight: disputed > 0 ? 600 : 'normal',
                                    }}
                                  >
                                    {disputed > 0 ? (
                                      <Box
                                        component="span"
                                        onClick={canAccessAllReviews ? (e) => {
                                          e.stopPropagation();
                                          const params = new URLSearchParams();
                                          if (dept.departmentId) params.set('departmentId', dept.departmentId);
                                          if (dept.departmentName) params.set('departmentName', dept.departmentName);
                                          params.set('status', 'underdispute');
                                          navigate(`/reviews/all?${params.toString()}`);
                                        } : undefined}
                                        sx={{ textDecoration: canAccessAllReviews ? 'underline' : 'none', cursor: canAccessAllReviews ? 'pointer' : 'default', '&:hover': canAccessAllReviews ? { opacity: 0.7 } : {} }}
                                      >
                                        {disputed}
                                      </Box>
                                    ) : (
                                      disputed
                                    )}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: excluded > 0 ? 'text.secondary' : 'inherit' }}>
                                    {excluded}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                                      {total > 0 ? (
                                        <>
                                          <Box
                                            component="span"
                                            sx={{
                                              width: 10,
                                              height: 10,
                                              borderRadius: '50%',
                                              bgcolor: ragColor,
                                              flexShrink: 0,
                                            }}
                                            title={ragStatus === 'green' ? 'On track (80%+ complete)' : ragStatus === 'amber' ? 'In progress (25–79%)' : 'Needs attention (<25% complete)'}
                                          />
                                          <span>{complete}</span>
                                          <Typography component="span" variant="body2" color="text.secondary">
                                            ({completionPct}%)
                                          </Typography>
                                        </>
                                      ) : (
                                        <Typography component="span" variant="body2" color="text.secondary">—</Typography>
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );})}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={deptReviewTableFilteredAndSorted.length}
                        page={deptReviewTablePage}
                        onPageChange={(_, newPage) => setDeptReviewTablePage(newPage)}
                        rowsPerPage={deptReviewTableRowsPerPage}
                        onRowsPerPageChange={(e) => {
                          setDeptReviewTableRowsPerPage(parseInt(e.target.value, 10));
                          setDeptReviewTablePage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                      />
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Managers that might need coaching – list (from feedback attestation; manager never sees) */}
              {(dashboardReporting?.managersNeedingCoaching?.length ?? 0) > 0 && (
                <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                  <Accordion
                    defaultExpanded={false}
                    sx={{
                      borderRadius: 1,
                      overflow: 'hidden',
                      background: 'linear-gradient(145deg, rgba(156, 39, 176, 0.06) 0%, rgba(123, 31, 162, 0.04) 100%)',
                      border: '1px solid',
                      borderColor: 'rgba(156, 39, 176, 0.2)',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchoolIcon color="primary" />
                        <Typography variant="h6" fontWeight="600">
                          Managers that might need coaching
                        </Typography>
                        <Chip
                          size="small"
                          label={`${dashboardReporting!.managersNeedingCoaching!.length} manager${dashboardReporting!.managersNeedingCoaching!.length !== 1 ? 's' : ''}`}
                          variant="outlined"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Direct reports indicated &quot;Some feedback received&quot; or &quot;Little to no feedback&quot; when acknowledging their review. Use this to prioritise coaching. Managers do not see what was said. Click &quot;View review&quot; to see the full employee comment.
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Manager</strong></TableCell>
                              <TableCell><strong>Employee</strong></TableCell>
                              <TableCell><strong>Cycle</strong></TableCell>
                              <TableCell><strong>Comment</strong></TableCell>
                              <TableCell align="right"><strong>Action</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dashboardReporting!.managersNeedingCoaching!.flatMap((m) =>
                              (m.reviews ?? []).length > 0
                                ? m.reviews.map((rev) => (
                                    <TableRow key={rev.reviewId}>
                                      <TableCell>{m.managerName}</TableCell>
                                      <TableCell>{rev.employeeName}</TableCell>
                                      <TableCell>{rev.reviewCycleName}</TableCell>
                                      <TableCell sx={{ maxWidth: 280 }} title={rev.feedbackAttestationComment ?? undefined}>
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
                                          {rev.feedbackAttestationComment || '—'}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() => navigate(`/reviews/${rev.reviewId}`)}
                                          sx={{ textTransform: 'none' }}
                                        >
                                          View review
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))
                                : [
                                    <TableRow key={m.managerId}>
                                      <TableCell>{m.managerName}</TableCell>
                                      <TableCell colSpan={4} align="right">{m.count} report{m.count !== 1 ? 's' : ''}</TableCell>
                                    </TableRow>,
                                  ]
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}

              {(adminCheckInData?.managerSummaries?.length ?? 0) > 0 && (
                <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                  <Box ref={managerReviewLoadSectionRef}>
                  <Accordion
                    expanded={managerReviewLoadExpanded}
                    onChange={(_, expanded) => setManagerReviewLoadExpanded(expanded)}
                    sx={{
                      borderRadius: 1,
                      overflow: 'hidden',
                      background: 'linear-gradient(145deg, rgba(67, 233, 123, 0.06) 0%, rgba(56, 249, 215, 0.05) 100%)',
                      boxShadow: '0 4px 20px rgba(67, 233, 123, 0.1)',
                      border: '1px solid',
                      borderColor: 'rgba(67, 233, 123, 0.2)',
                      transition: 'box-shadow 0.2s',
                      '&:hover': { boxShadow: '0 8px 30px rgba(67, 233, 123, 0.15)' },
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': { margin: 0 },
                      '& .MuiAccordionSummary-root': {
                        borderRadius: 2,
                        px: 3,
                        py: 2.5,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        '& .MuiAccordionSummary-content': { alignItems: 'center', my: 0 },
                        '& .MuiAccordionSummary-expandIconWrapper': { ml: 2 },
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <Box
                          sx={{
                            bgcolor: 'primary.main',
                            borderRadius: '50%',
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            transition: 'transform 0.2s',
                            '&:hover': {
                              bgcolor: 'primary.dark',
                              transform: 'scale(1.1)',
                            },
                          }}
                        >
                          <ExpandMoreIcon />
                        </Box>
                      }
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box
                          sx={{
                            bgcolor: 'primary.main',
                            borderRadius: 2,
                            p: 1,
                            mr: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <PeopleIcon sx={{ color: 'white' }} />
                        </Box>
                        <Typography variant="h6" fontWeight="600">
                          Manager Review Load
                        </Typography>
                        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip
                            size="small"
                            label={`${managerReviewLoadSummary.overThreshold} over threshold`}
                            color={managerReviewLoadSummary.red > 0 ? 'error' : managerReviewLoadSummary.amber > 0 ? 'warning' : 'success'}
                          />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Flags managers by direct report count to highlight potential review-process overload. Thresholds: Green 6 or fewer, Amber 7-9, Red 10 or more.
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="Search by manager name..."
                        value={managerTableSearch}
                        onChange={(e) => {
                          setManagerTableSearch(e.target.value);
                          setManagerTablePage(0);
                        }}
                        sx={{ mb: 2, maxWidth: 320 }}
                        fullWidth
                      />
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <Box
                                  component="span"
                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
                                  onClick={() => handleManagerTableSort('managerName')}
                                >
                                  <strong>Manager</strong>
                                  {getManagerTableSortIcon('managerName')}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Box
                                  component="span"
                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
                                  onClick={() => handleManagerTableSort('directReportCount')}
                                >
                                  <strong>Direct reports</strong>
                                  {getManagerTableSortIcon('directReportCount')}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Box
                                  component="span"
                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
                                  onClick={() => handleManagerTableSort('rag')}
                                >
                                  <strong>Review load</strong>
                                  {getManagerTableSortIcon('rag')}
                                </Box>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {managerTablePaginated.map((manager) => {
                              const rag = getManagerReviewLoadRag(manager.directReportCount);
                              const ragColor = rag === 'red' ? 'error.main' : rag === 'amber' ? 'warning.main' : 'success.main';
                              const ragLabel = rag === 'red' ? 'Overloaded' : rag === 'amber' ? 'Watch' : 'Manageable';
                              const ragHelp = rag === 'red'
                                ? 'Red (10+ direct reports)'
                                : rag === 'amber'
                                ? 'Amber (7-9 direct reports)'
                                : 'Green (0-6 direct reports)';
                              return (
                                <TableRow key={manager.managerId} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                  <TableCell>{manager.managerName}</TableCell>
                                  <TableCell align="right">{manager.directReportCount}</TableCell>
                                  <TableCell align="right">
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                                      <Box
                                        component="span"
                                        sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: ragColor, flexShrink: 0 }}
                                        title={ragHelp}
                                      />
                                      <Typography component="span" variant="body2">{ragLabel}</Typography>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={managerTableFilteredAndSorted.length}
                        page={managerTablePage}
                        onPageChange={(_, newPage) => setManagerTablePage(newPage)}
                        rowsPerPage={managerTableRowsPerPage}
                        onRowsPerPageChange={(e) => {
                          setManagerTableRowsPerPage(parseInt(e.target.value, 10));
                          setManagerTablePage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                      />
                    </AccordionDetails>
                  </Accordion>
                  </Box>
                </Grid>
              )}

            </Box>

            {/* IDP Status Section */}
            {idpDashboardReporting && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <SchoolIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                  <Typography variant="h5" fontWeight="700" color="primary">
                    Individual Development Plans
                  </Typography>
                </Box>

                <Grid container spacing={3} sx={{ mb: 3 }}>
                  {/* Total IDPs */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Card
                      onClick={canAccessAllReviews ? () => navigate('/individual-development-plan/all') : undefined}
                      sx={{
                        cursor: canAccessAllReviews ? 'pointer' : 'default',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)' },
                        height: '100%',
                      }}
                    >
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140, p: 3 }}>
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                            Total IDPs
                          </Typography>
                          {loading ? renderCardNumberSkeleton() : (
                            <Typography variant="h3" fontWeight="bold">
                              {idpDashboardReporting.totalIdps}
                            </Typography>
                          )}
                        </Box>
                        <SchoolIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end' }} />
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Outstanding */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Card
                      onClick={canAccessAllReviews ? () => navigate('/individual-development-plan/all?status=outstanding') : undefined}
                      sx={{
                        cursor: canAccessAllReviews ? 'pointer' : 'default',
                        background: 'linear-gradient(135deg, #e57373 0%, #ef5350 100%)',
                        color: 'white',
                        boxShadow: '0 8px 24px rgba(229, 115, 115, 0.3)',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(229, 115, 115, 0.4)' },
                        height: '100%',
                      }}
                    >
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140, p: 3 }}>
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                            Outstanding
                          </Typography>
                          {loading ? renderCardNumberSkeleton(96) : (
                            <Typography variant="h3" fontWeight="bold">
                              {idpDashboardReporting.totalOutstandingIdps}
                              {idpDashboardReporting.idpsByDepartment.length > 0 && (() => {
                                const totalExpected = idpDashboardReporting.idpsByDepartment.reduce((s, d) => s + (d.expectedEmployeeCount ?? 0), 0);
                                const pct = totalExpected > 0 ? Math.round((idpDashboardReporting.totalOutstandingIdps / totalExpected) * 100) : 0;
                                return pct > 0 ? ` (${pct}%)` : '';
                              })()}
                            </Typography>
                          )}
                        </Box>
                        <WarningAmberIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end' }} />
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Not Submitted */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Card
                      onClick={canAccessAllReviews ? () => navigate('/individual-development-plan/all?status=no-idp') : undefined}
                      sx={{
                        cursor: canAccessAllReviews ? 'pointer' : 'default',
                        background: 'linear-gradient(135deg, #ffb74d 0%, #ffa726 100%)',
                        color: 'white',
                        boxShadow: '0 8px 24px rgba(255, 183, 77, 0.3)',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(255, 183, 77, 0.4)' },
                        height: '100%',
                      }}
                    >
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140, p: 3 }}>
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                            Not Submitted
                          </Typography>
                          {loading ? renderCardNumberSkeleton() : (
                            <Typography variant="h3" fontWeight="bold">
                              {idpDashboardReporting.totalNotSubmittedIdps}
                            </Typography>
                          )}
                        </Box>
                        <EditIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end' }} />
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Acknowledged IDPs */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Card
                      onClick={canAccessAllReviews ? () => navigate('/individual-development-plan/all?status=acknowledged') : undefined}
                      sx={{
                        cursor: canAccessAllReviews ? 'pointer' : 'default',
                        background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                        color: 'white',
                        boxShadow: '0 8px 24px rgba(67, 233, 123, 0.3)',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(67, 233, 123, 0.4)' },
                        height: '100%',
                      }}
                    >
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140, p: 3 }}>
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                            Acknowledged IDPs
                            </Typography>
                            {loading ? renderCardNumberSkeleton() : (
                              <Typography variant="h3" fontWeight="bold">
                                {idpDashboardReporting.totalActiveIdps}
                              </Typography>
                            )}
                        </Box>
                        <CheckCircleIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end' }} />
                      </CardContent>
                    </Card>
                  </Grid>

                </Grid>

                {/* IDPs by Department */}
                {idpDashboardReporting.idpsByDepartment.length > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Accordion
                      defaultExpanded={false}
                      sx={{
                        borderRadius: 1,
                        overflow: 'hidden',
                        background: 'linear-gradient(145deg, rgba(255, 167, 38, 0.05) 0%, rgba(255, 112, 67, 0.04) 100%)',
                        boxShadow: '0 4px 20px rgba(255, 167, 38, 0.1)',
                        border: '1px solid',
                        borderColor: 'rgba(255, 167, 38, 0.15)',
                        transition: 'box-shadow 0.2s',
                        '&:hover': { boxShadow: '0 8px 30px rgba(255, 167, 38, 0.15)' },
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { margin: 0 },
                        '& .MuiAccordionSummary-root': {
                          borderRadius: 2,
                          px: 3,
                          py: 2.5,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          '& .MuiAccordionSummary-content': { alignItems: 'center', my: 0 },
                          '& .MuiAccordionSummary-expandIconWrapper': { ml: 2 },
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <Box
                            sx={{
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              width: 32,
                              height: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              transition: 'transform 0.2s',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                                transform: 'scale(1.1)',
                              },
                            }}
                          >
                            <ExpandMoreIcon />
                          </Box>
                        }
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Box
                            sx={{
                              bgcolor: 'primary.main',
                              borderRadius: 2,
                              p: 1,
                              mr: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <SchoolIcon sx={{ color: 'white' }} />
                          </Box>
                          <Typography variant="h6" fontWeight="600">
                            Status by Department
                          </Typography>
                          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip 
                              label={`${idpDashboardReporting.departmentCount ?? idpDashboardReporting.idpsByDepartment.length} department${(idpDashboardReporting.departmentCount ?? idpDashboardReporting.idpsByDepartment.length) !== 1 ? 's' : ''}`} 
                              size="small" 
                              variant="outlined"
                            />
                            {idpDashboardReporting.totalPendingIdps > 0 && (
                              <Chip 
                                label={`${idpDashboardReporting.totalPendingIdps} with manager`} 
                                color="warning" 
                                size="small" 
                              />
                            )}
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pb: 3 }}>
                        <TextField
                          size="small"
                          placeholder="Search by department name..."
                          value={idpDeptTableSearch}
                          onChange={(e) => {
                            setIdpDeptTableSearch(e.target.value);
                            setIdpDeptTablePage(0);
                          }}
                          sx={{ mb: 2, maxWidth: 320 }}
                          fullWidth
                        />
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>
                                  <Box
                                    component="span"
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
                                    onClick={() => handleIdpDeptTableSort('departmentName')}
                                  >
                                    <strong>Department</strong>
                                    {getIdpDeptSortIcon('departmentName')}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Box
                                    component="span"
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
                                    onClick={() => handleIdpDeptTableSort('total')}
                                  >
                                    <strong>Total</strong>
                                    {getIdpDeptSortIcon('total')}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Box
                                    component="span"
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
                                    onClick={() => handleIdpDeptTableSort('pending')}
                                  >
                                    <strong>With manager</strong>
                                    {getIdpDeptSortIcon('pending')}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Box
                                    component="span"
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
                                    onClick={() => handleIdpDeptTableSort('active')}
                                  >
                                    <strong>Acknowledged</strong>
                                    {getIdpDeptSortIcon('active')}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Box
                                    component="span"
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }}
                                    onClick={() => handleIdpDeptTableSort('outstanding')}
                                  >
                                    <strong>Outstanding %</strong>
                                    {getIdpDeptSortIcon('outstanding')}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {idpDeptTableFilteredAndSorted
                                .slice(
                                  idpDeptTablePage * idpDeptTableRowsPerPage,
                                  idpDeptTablePage * idpDeptTableRowsPerPage + idpDeptTableRowsPerPage
                                )
                                .map(({ dept, total, pending, active, outstanding, outstandingPct, expected }) => {
                                  const ragStatus = expected === 0 ? 'grey' as const
                                    : outstandingPct < 20 ? 'green' as const
                                    : outstandingPct < 80 ? 'amber' as const
                                    : 'red' as const;
                                  const ragColor = ragStatus === 'green' ? 'success.main' : ragStatus === 'amber' ? 'warning.main' : ragStatus === 'red' ? 'error.main' : 'grey.400';
                                  return (
                                    <TableRow 
                                      key={dept.departmentId || dept.departmentName || 'no-dept'}
                                      sx={{ cursor: canAccessAllReviews ? 'pointer' : 'default', '&:hover': canAccessAllReviews ? { bgcolor: 'action.hover' } : {} }}
                                      onClick={canAccessAllReviews ? () => {
                                        const params = new URLSearchParams();
                                        if (dept.departmentId) params.set('departmentId', dept.departmentId);
                                        if (dept.departmentName) params.set('departmentName', dept.departmentName);
                                        navigate(`/individual-development-plan/all?${params.toString()}`);
                                      } : undefined}
                                    >
                                      <TableCell>{dept.departmentName}</TableCell>
                                      <TableCell align="right">{total}</TableCell>
                                      <TableCell align="right">
                                        {pending > 0 ? (
                                          <Box
                                            component="span"
                                            onClick={canAccessAllReviews ? (e) => {
                                              e.stopPropagation();
                                              const params = new URLSearchParams();
                                              if (dept.departmentId) params.set('departmentId', dept.departmentId);
                                              if (dept.departmentName) params.set('departmentName', dept.departmentName);
                                              params.set('status', 'withmanager');
                                              navigate(`/individual-development-plan/all?${params.toString()}`);
                                            } : undefined}
                                            sx={{
                                              color: canAccessAllReviews ? 'warning.main' : 'inherit',
                                              fontWeight: 600,
                                              textDecoration: canAccessAllReviews ? 'underline' : 'none',
                                              cursor: canAccessAllReviews ? 'pointer' : 'default',
                                              '&:hover': canAccessAllReviews ? { opacity: 0.8 } : {},
                                            }}
                                            title={canAccessAllReviews ? 'View IDPs with manager for this department' : undefined}
                                          >
                                            {pending}
                                          </Box>
                                        ) : (
                                          pending
                                        )}
                                      </TableCell>
                                      <TableCell align="right">{active}</TableCell>
                                      <TableCell align="right">
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                                          {expected > 0 ? (
                                            <>
                                              <Box
                                                component="span"
                                                sx={{
                                                  width: 10,
                                                  height: 10,
                                                  borderRadius: '50%',
                                                  bgcolor: ragColor,
                                                  flexShrink: 0,
                                                }}
                                                title={ragStatus === 'green' ? 'On track (<20% outstanding)' : ragStatus === 'amber' ? 'In progress (20–79% outstanding)' : 'Needs attention (80%+ outstanding)'}
                                              />
                                              <span>{outstanding}</span>
                                              <Typography component="span" variant="body2" color="text.secondary">
                                                ({outstandingPct}%)
                                              </Typography>
                                            </>
                                          ) : (
                                            <Typography component="span" variant="body2" color="text.secondary">—</Typography>
                                          )}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={idpDeptTableFilteredAndSorted.length}
                          page={idpDeptTablePage}
                          onPageChange={(_, newPage) => setIdpDeptTablePage(newPage)}
                          rowsPerPage={idpDeptTableRowsPerPage}
                          onRowsPerPageChange={(e) => {
                            setIdpDeptTableRowsPerPage(parseInt(e.target.value, 10));
                            setIdpDeptTablePage(0);
                          }}
                          rowsPerPageOptions={[5, 10, 25, 50]}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}
              </Box>
            )}

            {/* Leave Section */}
            <Box sx={{ mb: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <EventAvailableIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                <Typography variant="h5" fontWeight="700" color="primary">
                  Leave
                </Typography>
              </Box>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card
                    onClick={() => navigate('/leave/organisation')}
                    sx={{
                      cursor: 'pointer',
                      background: (orgLeaveAtRisk?.length ?? 0) > 0
                        ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                        : 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(17, 153, 142, 0.3)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      height: '100%',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(17, 153, 142, 0.4)' },
                    }}
                  >
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                          ZA statutory leave at risk
                        </Typography>
                        {orgLeaveCardsLoading ? (
                          <>
                            {renderCardNumberSkeleton()}
                            <Skeleton variant="text" width={160} height={22} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                          </>
                        ) : (
                          <>
                            <Typography variant="h3" fontWeight="bold">
                              {zaLeaveAtRiskCount}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.85, mt: 1 }}>
                              {zaLeaveAtRiskCount === 1 ? 'employee' : 'employees'} below ZA statutory target
                            </Typography>
                          </>
                        )}
                      </Box>
                      <EventAvailableIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end' }} />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card
                    onClick={() => navigate('/leave/organisation')}
                    sx={{
                      cursor: 'pointer',
                      background: gbLeaveAtRiskCount > 0
                        ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                        : 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(17, 153, 142, 0.3)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      height: '100%',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(17, 153, 142, 0.4)' },
                    }}
                  >
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                          UK statutory leave at risk
                        </Typography>
                        {orgLeaveCardsLoading ? (
                          <>
                            {renderCardNumberSkeleton()}
                            <Skeleton variant="text" width={160} height={22} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                          </>
                        ) : (
                          <>
                            <Typography variant="h3" fontWeight="bold">
                              {gbLeaveAtRiskCount}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.85, mt: 1 }}>
                              {gbLeaveAtRiskCount === 1 ? 'employee' : 'employees'} below UK statutory target
                            </Typography>
                          </>
                        )}
                      </Box>
                      <EventAvailableIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end' }} />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card
                    onClick={() => navigate('/leave/organisation')}
                    sx={{
                      cursor: 'pointer',
                      background: inLeaveAtRiskCount > 0
                        ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                        : 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(17, 153, 142, 0.3)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      height: '100%',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(17, 153, 142, 0.4)' },
                    }}
                  >
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                          IN statutory leave at risk
                        </Typography>
                        {orgLeaveCardsLoading ? (
                          <>
                            {renderCardNumberSkeleton()}
                            <Skeleton variant="text" width={160} height={22} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                          </>
                        ) : (
                          <>
                            <Typography variant="h3" fontWeight="bold">
                              {inLeaveAtRiskCount}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.85, mt: 1 }}>
                              {inLeaveAtRiskCount === 1 ? 'employee' : 'employees'} below IN statutory target
                            </Typography>
                          </>
                        )}
                      </Box>
                      <EventAvailableIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end' }} />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Accordion
                  defaultExpanded={false}
                  sx={{
                    borderRadius: 1,
                    overflow: 'hidden',
                    background: 'linear-gradient(145deg, rgba(17, 153, 142, 0.06) 0%, rgba(56, 239, 125, 0.04) 100%)',
                    boxShadow: '0 4px 20px rgba(17, 153, 142, 0.1)',
                    border: '1px solid',
                    borderColor: 'rgba(17, 153, 142, 0.2)',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: '0 8px 30px rgba(17, 153, 142, 0.15)' },
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { margin: 0 },
                    '& .MuiAccordionSummary-root': {
                      borderRadius: 2,
                      px: 3,
                      py: 2.5,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      '& .MuiAccordionSummary-content': { alignItems: 'center', my: 0 },
                      '& .MuiAccordionSummary-expandIconWrapper': { ml: 2 },
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <Box
                        sx={{
                          bgcolor: 'primary.main',
                          borderRadius: '50%',
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          transition: 'transform 0.2s',
                          '&:hover': { bgcolor: 'primary.dark', transform: 'scale(1.1)' },
                        }}
                      >
                        <ExpandMoreIcon />
                      </Box>
                    }
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ bgcolor: 'primary.main', borderRadius: 2, p: 1, mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <EventAvailableIcon sx={{ color: 'white' }} />
                      </Box>
                      <Typography variant="h6" fontWeight="600">
                        Statutory leave at risk
                      </Typography>
                      <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip
                          label={(orgLeaveAtRisk?.length ?? 0) === 1 ? '1 employee' : `${orgLeaveAtRisk?.length ?? 0} employees`}
                          size="small"
                          variant="outlined"
                          color={(orgLeaveAtRisk?.length ?? 0) > 0 ? 'warning' : 'default'}
                        />
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3, pb: 3 }}>
                    {orgLeaveAtRisk === null ? (
                      <Typography variant="body2" color="text.secondary">Loading…</Typography>
                    ) : orgLeaveAtRisk.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          All employees have met their country statutory annual leave targets this year.
                        </Typography>
                        <Button size="small" sx={{ mt: 2 }} onClick={() => navigate('/leave/organisation')}>
                          View organisation leave
                        </Button>
                        {EXPENSE_CLAIMS_ENABLED && (
                          <Button size="small" sx={{ mt: 2, ml: 1 }} onClick={() => navigate('/expenses/organisation')}>
                            View organisation expenses
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <>
                        <TextField
                          size="small"
                          placeholder="Search by name or department..."
                          value={orgLeaveTableSearch}
                          onChange={(e) => { setOrgLeaveTableSearch(e.target.value); setOrgLeaveTablePage(0); }}
                          sx={{ mb: 2, maxWidth: 320 }}
                          fullWidth
                        />
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleOrgLeaveTableSort('departmentName')}>
                                    <strong>Department</strong>
                                    {getOrgLeaveSortIcon('departmentName')}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleOrgLeaveTableSort('employeeName')}>
                                    <strong>Employee</strong>
                                    {getOrgLeaveSortIcon('employeeName')}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', justifyContent: 'flex-end', '&:hover': { opacity: 0.7 } }} onClick={() => handleOrgLeaveTableSort('daysTaken')}>
                                    <strong>Days taken</strong>
                                    {getOrgLeaveSortIcon('daysTaken')}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', justifyContent: 'flex-end', '&:hover': { opacity: 0.7 } }} onClick={() => handleOrgLeaveTableSort('daysToTake')}>
                                    <strong>Days to take</strong>
                                    {getOrgLeaveSortIcon('daysToTake')}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {orgLeaveTableFilteredAndSorted
                                .slice(
                                  orgLeaveTablePage * orgLeaveTableRowsPerPage,
                                  orgLeaveTablePage * orgLeaveTableRowsPerPage + orgLeaveTableRowsPerPage
                                )
                                .map((r) => (
                                  <TableRow key={r.employeeId} hover>
                                    <TableCell>{r.departmentName ?? '—'}</TableCell>
                                    <TableCell>{r.employeeName}</TableCell>
                                    <TableCell align="right">{r.annualDaysTaken}</TableCell>
                                    <TableCell align="right">{r.daysStillToTake}</TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={orgLeaveTableFilteredAndSorted.length}
                          page={orgLeaveTablePage}
                          onPageChange={(_, newPage) => setOrgLeaveTablePage(newPage)}
                          rowsPerPage={orgLeaveTableRowsPerPage}
                          onRowsPerPageChange={(e) => {
                            setOrgLeaveTableRowsPerPage(parseInt(e.target.value, 10));
                            setOrgLeaveTablePage(0);
                          }}
                          rowsPerPageOptions={[5, 10, 25, 50]}
                          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                        />
                        <Box sx={{ mt: 2 }}>
                          <Button size="small" variant="outlined" onClick={() => navigate('/leave/organisation')}>
                            View full report
                          </Button>
                        </Box>
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Box>

            {/* Check-Ins Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CheckCircleIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                <Typography variant="h5" fontWeight="700" color="primary">
                  Check-Ins
                </Typography>
              </Box>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Overdue - palette: orange (same as IDP/warning cards elsewhere) */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card
                    sx={{
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(255, 167, 38, 0.4)' },
                      height: '100%',
                      background: 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)',
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(255, 167, 38, 0.3)',
                    }}
                  >
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 130 }}>
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.95, mb: 1 }}>
                          Overdue
                        </Typography>
                        <Typography variant="h3" fontWeight="bold">
                          {adminCheckInData?.totalOverdue ?? 0}
                        </Typography>
                      </Box>
                      <CalendarTodayIcon sx={{ fontSize: 40, opacity: 0.9, alignSelf: 'flex-end' }} />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Late - palette: blue/teal (same as To Manage card) */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card
                    sx={{
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(79, 172, 254, 0.4)' },
                      height: '100%',
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(79, 172, 254, 0.3)',
                    }}
                  >
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 130 }}>
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.95, mb: 1 }}>
                          Late
                        </Typography>
                        <Typography variant="h3" fontWeight="bold">
                          {adminCheckInData?.totalLate ?? 0}
                        </Typography>
                      </Box>
                      <PendingIcon sx={{ fontSize: 40, opacity: 0.9, alignSelf: 'flex-end' }} />
                    </CardContent>
                  </Card>
                </Grid>

                {/* Missed - palette: pink/coral (same as Your team and their teams card) */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Card
                    sx={{
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(245, 87, 108, 0.4)' },
                      height: '100%',
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(245, 87, 108, 0.3)',
                    }}
                  >
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 130 }}>
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.95, mb: 1 }}>
                          Missed
                        </Typography>
                        <Typography variant="h3" fontWeight="bold">
                          {adminCheckInData?.totalMissed ?? 0}
                        </Typography>
                      </Box>
                      <AssignmentIcon sx={{ fontSize: 40, opacity: 0.9, alignSelf: 'flex-end' }} />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {((adminCheckInData?.managerSummaries ?? []).filter((m) => m.hasOptedOutOfCheckIns).length > 0) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Opted out — off-system
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    These managers have turned off built-in check-ins and manage ongoing 1:1s outside Aardvark. They are not counted as incomplete setup.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {(adminCheckInData?.managerSummaries ?? [])
                      .filter((m) => m.hasOptedOutOfCheckIns)
                      .map((m) => (
                        <Chip
                          key={m.managerId}
                          size="small"
                          label={m.managerName}
                          variant="outlined"
                        />
                      ))}
                  </Box>
                </Alert>
              )}

            </Box>
              </>
            )
          )}

          {/* Team Dashboard View */}
          {activeView === 'team' && isManager && (
            loading ? renderDashboardLoadingView('team') : (
            <>
              {/* Reviews Section */}
              <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                  <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  <Typography variant="h5" fontWeight="700" color="primary">
                    Reviews
                  </Typography>
                  {reviewCycles.length > 0 && (
                    <FormControl size="small" sx={{ minWidth: 260, ml: 'auto' }}>
                      <InputLabel shrink id="team-dashboard-cycle-filter-label">Review cycle</InputLabel>
                      <Select
                        labelId="team-dashboard-cycle-filter-label"
                        id="team-dashboard-cycle-filter"
                        value={dashboardCycleFilter}
                        label="Review cycle"
                        onChange={(e) => setDashboardCycleFilter(e.target.value)}
                        displayEmpty
                        notched
                        renderValue={(value) => {
                          if (value === '') return activeCycles.length > 0 ? `All active cycles (${activeCycles.length})` : 'Select a cycle';
                          const cycle = allCyclesSorted.find((c) => c.id === value);
                          if (!cycle) return value;
                          const range = cycle.startDate && cycle.endDate ? ` (${formatDateRange(cycle.startDate, cycle.endDate)})` : '';
                          return cycle.isActive ? `${cycle.name}${range}` : `${cycle.name}${range} (Closed)`;
                        }}
                      >
                        {activeCycles.length > 0 && (
                          <MenuItem value="">
                            All active cycles ({activeCycles.length})
                          </MenuItem>
                        )}
                        {allCyclesSorted.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                            {c.startDate && c.endDate && ` (${formatDateRange(c.startDate, c.endDate)})`}
                            {!c.isActive && ' (Closed)'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>

                <Grid container spacing={3} sx={{ mb: 3 }}>
<Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card
                    onClick={() => navigate('/review-cycles')}
                    sx={{
                      cursor: 'pointer',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)' },
                    }}
                  >
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                          Current Cycle
                        </Typography>
                        {selectedCycleForDisplay ? (
                          <>
                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, lineHeight: 1.2 }}>
                              {selectedCycleForDisplay.name}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mb: 0.5 }}>
                              {formatDateRange(selectedCycleForDisplay.startDate, selectedCycleForDisplay.endDate)}
                            </Typography>
                        {selectedCycleForDisplay.employeeCutoff && (
                              <>
                                <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mb: 0.5 }}>
                                  <strong>Employee cutoff:</strong> {formatDate(selectedCycleForDisplay.employeeCutoff)}
                                </Typography>
                                {(() => {
                                  const daysRemaining = calculateDaysRemaining(selectedCycleForDisplay);
                                  if (daysRemaining !== null) {
                                    return (
                                      <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }}>
                                        {daysRemaining > 0
                                          ? `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} until cutoff`
                                          : daysRemaining === 0
                                          ? 'Cutoff is today'
                                          : `${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) !== 1 ? 's' : ''} past cutoff`}
                                      </Typography>
                                    );
                                  }
                                  return null;
                                })()}
                              </>
                            )}
                          </>
                        ) : (
                          <Typography variant="body1" sx={{ opacity: 0.85 }}>
                            No active cycle
                          </Typography>
                        )}
                      </Box>
                      <CalendarTodayIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end', mt: 1 }} />
                    </CardContent>
                  </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card
                    onClick={() => navigate(dashboardCycleFilter ? `/reviews/to-manage?cycleId=${encodeURIComponent(dashboardCycleFilter)}` : '/reviews/to-manage')}
                    sx={{
                      cursor: 'pointer',
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(79, 172, 254, 0.3)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(79, 172, 254, 0.4)' },
                    }}
                  >
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                          Awaiting my review
                        </Typography>
                        {reviewsLoading ? (
                          <Skeleton variant="text" width={56} height={52} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                        ) : (
                          <Typography variant="h3" fontWeight="bold">
                            {pendingDirectReports.length}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', mt: 2 }}>
                        <PersonIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Your team and their teams card - reviews in your full hierarchy (view-only); only show for Managers/Senior Managers/Admins (not Team Leads who only have direct reports) */}
                {!isTeamLeadOnly && (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                      onClick={handleTeamReviewsClick}
                      sx={{
                        cursor: 'pointer',
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: 'white',
                        boxShadow: '0 8px 24px rgba(245, 87, 108, 0.3)',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(245, 87, 108, 0.4)' },
                      }}
                    >
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                            Your team and their teams
                          </Typography>
                          {reviewsLoading ? (
                            <Skeleton variant="text" width={72} height={52} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                          ) : (
                            <Typography variant="h3" fontWeight="bold">
                              {totalReportingLineInScopeCount}
                              {disputedCount > 0 && (
                                <Typography component="span" variant="body2" sx={{ ml: 1, opacity: 0.9, fontWeight: 400 }}>
                                  ({disputedCount} under dispute)
                                </Typography>
                              )}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', mt: 2 }}>
                          <AssessmentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                      color: 'white',
                      boxShadow: '0 8px 24px rgba(67, 233, 123, 0.3)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(67, 233, 123, 0.4)' },
                    }}
                  >
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                      <Box sx={{ flex: 1 }}>
                        <Tooltip title="Team and company both use staged progress: Not started 0%, In progress 25%, Self-review submitted 50%, Awaiting discussion/finalisation 75%, Finalised 100%." placement="top">
                          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                            Team Completion
                          </Typography>
                        </Tooltip>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                          {reviewsLoading ? (
                            <Skeleton variant="text" width={80} height={52} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                          ) : (
                            <Typography variant="h3" fontWeight="bold">
                              {teamCompletionRate}%
                            </Typography>
                          )}
                          {dashboardReporting && orgCompletionRate !== null && (
                            <Typography variant="body2" sx={{ opacity: 0.85, fontSize: '0.9rem' }}>
                              (Company: {orgCompletionRate}%)
                            </Typography>
                          )}
                        </Box>
                        {reviewsLoading ? (
                          <Skeleton variant="rounded" height={6} sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.4)' }} />
                        ) : (
                          <LinearProgress
                            variant="determinate"
                            value={teamCompletionRate}
                            sx={{
                              mt: 1,
                              height: 6,
                              borderRadius: 3,
                              bgcolor: 'rgba(255,255,255,0.3)',
                              '& .MuiLinearProgress-bar': { bgcolor: 'white' },
                            }}
                          />
                        )}
                        {dashboardReporting && orgCompletionRate !== null && (
                          <Box sx={{ mt: 1.5 }}>
                            <LinearProgress
                              variant="determinate"
                              value={orgCompletionRate}
                              sx={{
                                height: 4,
                                borderRadius: 2,
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '& .MuiLinearProgress-bar': { bgcolor: 'rgba(255,255,255,0.6)' },
                              }}
                            />
                            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem', mt: 0.5, display: 'block' }}>
                              Company completion rate
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              </Box>

              {/* Individual Development Plans Section */}
              {userRoles.some((r: string) => r === 'Manager' || r === 'SeniorManager' || r === 'TeamLead' || r === 'Administrator') && (
                <Box sx={{ mb: 5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <SchoolIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                    <Typography variant="h5" fontWeight="700" color="primary">
                      Individual Development Plans
                    </Typography>
                  </Box>

                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Card
                        onClick={() => navigate('/individual-development-plan?team=true')}
                        sx={{
                          cursor: 'pointer',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(102, 126, 234, 0.4)' },
                        }}
                      >
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                              Total IDPs
                            </Typography>
                            {loading ? renderCardNumberSkeleton() : (
                              <Typography variant="h3" fontWeight="bold">
                                {teamIdpDashboardReporting?.totalIdps ?? 0}
                              </Typography>
                            )}
                          </Box>
                          <SchoolIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end' }} />
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Card
                        onClick={() => navigate('/individual-development-plan?team=true&status=outstanding')}
                        sx={{
                          cursor: 'pointer',
                          background: 'linear-gradient(135deg, #e57373 0%, #ef5350 100%)',
                          color: 'white',
                          boxShadow: '0 8px 24px rgba(229, 115, 115, 0.3)',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(229, 115, 115, 0.4)' },
                        }}
                      >
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                              Outstanding
                            </Typography>
                            {loading ? renderCardNumberSkeleton() : (
                              <Typography variant="h3" fontWeight="bold">
                                {teamIdpDashboardReporting?.totalOutstandingIdps ?? 0}
                              </Typography>
                            )}
                          </Box>
                          <WarningAmberIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end' }} />
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Card
                        onClick={() => navigate('/individual-development-plan?team=true&status=draft')}
                        sx={{
                          cursor: 'pointer',
                          background: 'linear-gradient(135deg, #ffb74d 0%, #ffa726 100%)',
                          color: 'white',
                          boxShadow: '0 8px 24px rgba(255, 183, 77, 0.3)',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(255, 183, 77, 0.4)' },
                        }}
                      >
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                              Not Submitted
                            </Typography>
                            {loading ? renderCardNumberSkeleton() : (
                              <Typography variant="h3" fontWeight="bold">
                                {teamIdpDashboardReporting?.totalNotSubmittedIdps ?? 0}
                              </Typography>
                            )}
                          </Box>
                          <EditIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end' }} />
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Card
                        onClick={() => navigate('/individual-development-plan?pending-approvals=true&status=withmanager')}
                        sx={{
                          cursor: 'pointer',
                          background: 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)',
                          color: 'white',
                          boxShadow: '0 8px 24px rgba(255, 167, 38, 0.3)',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(255, 167, 38, 0.4)' },
                        }}
                      >
                        <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                              With me
                            </Typography>
                            {loading ? renderCardNumberSkeleton() : (
                              <Typography variant="h3" fontWeight="bold">
                                {teamIdpDashboardReporting?.totalPendingIdps ?? 0}
                              </Typography>
                            )}
                          </Box>
                          <PendingIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end' }} />
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Leave Section */}
              <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <EventAvailableIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                  <Typography variant="h5" fontWeight="700" color="primary">
                    Leave
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                      onClick={() => navigate('/team-leave')}
                      sx={{
                        cursor: 'pointer',
                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        color: 'white',
                        boxShadow: '0 8px 24px rgba(17, 153, 142, 0.3)',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(17, 153, 142, 0.4)' },
                      }}
                    >
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                            Statutory leave to take
                          </Typography>
                          {loading ? (
                            <>
                              {renderCardNumberSkeleton()}
                              <Skeleton variant="text" width={180} height={22} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                            </>
                          ) : teamLeaveBurndown !== null ? (
                            <>
                              <Typography variant="h3" fontWeight="bold">
                                {teamLeaveBurndown.length}
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.95, mt: 0.5 }}>
                                {teamLeaveBurndown.length === 1
                                  ? 'team member'
                                  : 'team members'} with statutory leave still to take
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                              View team leave
                            </Typography>
                          )}
                        </Box>
                        <EventAvailableIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end', mt: 1 }} />
                      </CardContent>
                    </Card>
                  </Grid>
                  {teamLeaveOver20 != null && teamLeaveOver20.length > 0 && (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Card
                        sx={{
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          color: 'white',
                          boxShadow: '0 8px 24px rgba(245, 158, 11, 0.3)',
                        }}
                      >
                        <CardContent>
                          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                            Over 20 days leave taken
                          </Typography>
                          {loading ? (
                            <>
                              {renderCardNumberSkeleton()}
                              <Skeleton variant="text" width={220} height={22} sx={{ bgcolor: 'rgba(255,255,255,0.4)' }} />
                            </>
                          ) : (
                            <>
                              <Typography variant="h3" fontWeight="bold">
                                {teamLeaveOver20.length}
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.95, mt: 0.5 }}>
                                {teamLeaveOver20.length === 1 ? 'team member has' : 'team members have'} taken more than 20 days (informational only)
                              </Typography>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Check-Ins Section */}
              <Box sx={{ mb: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <CheckCircleIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                  <Typography variant="h5" fontWeight="700" color="primary">
                    Check-Ins
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                      onClick={() => navigate('/checkins')}
                      sx={{
                        cursor: 'pointer',
                        background: 'linear-gradient(135deg, #0288d1 0%, #00bcd4 100%)',
                        color: 'white',
                        boxShadow: '0 8px 24px rgba(2, 136, 209, 0.3)',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(2, 136, 209, 0.4)' },
                      }}
                    >
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 140 }}>
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                            Next check-in
                          </Typography>
                          {nextTeamCheckIn ? (
                            <>
                              <Typography variant="body1" fontWeight="600" sx={{ lineHeight: 1.3 }}>
                                {nextTeamCheckIn.employeeName}
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mt: 0.5 }}>
                                Due {formatDate(nextTeamCheckIn.dueDate)}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                              No upcoming check-in
                            </Typography>
                          )}
                        </Box>
                        <CalendarTodayIcon sx={{ fontSize: 40, opacity: 0.8, alignSelf: 'flex-end', mt: 1 }} />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

            </>
            )
          )}


          {/* Create Review Cycle Dialog */}
          <Dialog
            open={createDialogOpen}
            onClose={() => !creating && setCreateDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: { borderRadius: 2 },
            }}
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              <Typography variant="h6" fontWeight="600">
                Create Review Cycle
              </Typography>
              <IconButton
                onClick={() => setCreateDialogOpen(false)}
                disabled={creating}
                sx={{ color: 'grey.500' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {createError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError('')}>
                  {createError}
                </Alert>
              )}
              {createDovetailInfo && !createError && (
                <Alert severity="info" sx={{ mb: 2 }} onClose={() => setCreateDovetailInfo('')}>
                  {createDovetailInfo}
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Quick suggestions:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {[1, 2, 3, 4].map((q) => {
                        const currentFY = currentFiscalYear ?? 0;
                        return (
                          <Button
                            key={q}
                            size="small"
                            variant="outlined"
                            onClick={() => handleSuggestFiscalQuarter(currentFY, q)}
                            disabled={creating || currentFiscalYear === null}
                            sx={{ textTransform: 'none', minWidth: 'auto', px: 1.5 }}
                          >
                            FY {currentFY} Q{q}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>
                  <TextField
                    label="Cycle Name"
                    fullWidth
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    disabled={creating}
                    required
                    placeholder="e.g., FY 2024 Q3 Review"
                  />
                </Box>
                <AppDatePicker
                  label="Start Date"
                  value={createForm.startDate}
                  onChange={handleStartDateChange}
                  disabled={creating}
                  shouldDisableDate={shouldDisableDate}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
                <AppDatePicker
                  label="End Date"
                  value={createForm.endDate}
                  onChange={(date) => {
                    setCreateDovetailInfo('');
                    setCreateForm({ ...createForm, endDate: date });
                  }}
                  disabled={creating}
                  shouldDisableDate={shouldDisableDate}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
                <AppDatePicker
                  label="Employee cutoff"
                  value={createForm.employeeCutoff}
                  onChange={(date) => {
                    setCreateDovetailInfo('');
                    setCreateForm({ ...createForm, employeeCutoff: date });
                  }}
                  disabled={creating}
                  shouldDisableDate={shouldDisableDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      helperText: 'Date by which employees must complete their part (e.g. self-review) so HR and managers can complete theirs.',
                    },
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, pt: 1 }}>
              <Button onClick={() => setCreateDialogOpen(false)} disabled={creating} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateReviewCycle}
                variant="contained"
                disabled={creating}
                sx={{
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  },
                }}
              >
                {creating ? <CircularProgress size={20} /> : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>

        {/* Dashboard Help Sidebar */}
        {showLegacyHelp && (
          <DashboardHelpSidebar
            isExpanded={helpExpanded}
            onToggle={() => setHelpExpanded(!helpExpanded)}
            viewType={activeView}
          />
        )}
      </AppLayout>
    </LocalizationProvider>
  );
}
